import { Base } from './Base'
import FaableAuthApi from './FaableAuthApi'
import { buildAndSubmitForm, resolveResponseType } from './lib/auth_helpers'
import { BroadcastSync } from './lib/broadcast_sync'
import { hasPermissions, hasRoles } from './lib/claims'
import { EXPIRY_MARGIN, STORAGE_KEY } from './lib/constants'
import {
  AuthApiError,
  AuthError,
  AuthImplicitGrantRedirectError,
  AuthInvalidTokenResponseError,
  AuthLoginRequiredError,
  AuthPKCEGrantCodeExchangeError,
  AuthRetryableFetchError,
  AuthSessionMissingError,
  AuthUnknownError,
  isAuthApiError,
  isAuthError,
  isAuthRetryableFetchError
} from './lib/errors'
import { _get, _post } from './lib/fetch'
import { document, fetch, window } from './lib/globals'
import {
  Deferred,
  RawAuthResponse,
  _mfaChallenge,
  _sessionResponse,
  checkExpiresInTime,
  getCodeChallengeAndMethod,
  isBrowser,
  retryable,
  sleep
} from './lib/helpers'
import { windowHelpers } from './lib/helpers/window'
import { decodeJWTPayload, expiryFromAccessToken } from './lib/jwt'
import {
  LastUsedLoginMethod,
  LastUsedLoginMethodKind,
  consumeLoginAttempt,
  loadLastUsedMethod,
  saveLastUsedMethod,
  saveLoginAttempt
} from './lib/last_used_storage'
import { getSessionFromCookies } from './lib/nextjs'
import {
  type PendingPasswordless,
  clearPendingPasswordless,
  loadPendingPasswordless,
  savePendingPasswordless
} from './lib/pending_passwordless'
import { loadCodeVerifier } from './lib/pkce_storage'
import { isValidSession } from './lib/session_helpers'
import { cookieStorageAdapter } from './lib/storage/cookie-storage'
import { localStorageAdapter } from './lib/storage/local-storage'
import { getItemAsync, setItemAsync } from './lib/storage_helpers'
import {
  AuthFlowType,
  AuthResult,
  CallRefreshTokenResult,
  GetTokenSilently,
  InitializeResult,
  JwtClaims,
  LastUsedCookieOptions,
  OAuthResponse,
  SignInWithOAuthConnection,
  SignupLoginOptions,
  SignupParams,
  SignupResult,
  Subscription,
  SupportedStorage,
  User
} from './lib/types'
import {
  AuthChangeEvent,
  AuthResponse,
  FaableAuthClientConfig
} from './lib/types'
import {
  ProfileUpdate,
  Session,
  SignOut,
  UserCredentials,
  UserSessions
} from './lib/types'
import {
  CALLBACK_MARKERS,
  callbackParamsToClear,
  clearURLParameters,
  parseParametersFromURL
} from './lib/url_helpers'
import { commit, version } from './lib/version'
import { withTimeout } from './lib/with_timeout'
import { Lock } from './lock/Lock'
import { LockAcquireTimeoutError } from './lock/locks'
import { getDomain, getTokenIssuer } from './utils'

// OIDC Core §3.1.2.6 — the errors a `prompt=none` request comes back with
// when the server could not sign the user in without a screen.
const SILENT_AUTH_ERRORS = [
  'login_required',
  'interaction_required',
  'consent_required',
  'account_selection_required'
]

export { cookieStorageAdapter, getSessionFromCookies }

/** Current session will be checked for refresh at this interval. */
const AUTO_REFRESH_TICK_DURATION = 30 * 1000

/**
 * A token refresh will be attempted this many ticks before the current session expires. */
const AUTO_REFRESH_TICK_THRESHOLD = 3

/**
 * Hard upper bound for a single `_callRefreshToken`, backoff included.
 *
 * DERIVED from the inner budget on purpose. `_refreshAccessToken` retries with
 * exponential backoff and stops only when the next sleep would overflow
 * `AUTO_REFRESH_TICK_DURATION` — which allows about 25s of sleeps plus eight
 * real requests. This used to be an independent `30 * 1000`, numerically equal
 * to that budget by coincidence, so a perfectly healthy retry sequence crossed
 * the deadline and was converted into a hard failure at second 30.
 *
 * It races, it does not abort: nothing cancels the in-flight fetch (there is no
 * AbortController in lib/fetch), so an abandoned attempt keeps running.
 */
const REFRESH_TIMEOUT_MS = AUTO_REFRESH_TICK_DURATION + 10 * 1000

/**
 * Storage-key suffix for the consume-once sign-out reason
 * ({@link FaableAuthClient.getSignOutReason}). Shares the session's
 * `storageKey` prefix so multiple clients on one origin don't cross-read.
 */
const SIGNOUT_REASON_SUFFIX = '-signout-reason'

/**
 * Error the auth server returned via redirect (`?error=...&
 * error_description=...`, RFC 6749 §4.1.2.1) on the page we initialized on,
 * with what this browser remembers of the round trip that failed.
 *
 * @see {@link FaableAuthClient.getRedirectError}
 */
export type RedirectError = {
  error: string
  error_description: string
  /** Stable machine code the server attached (`identity_already_linked`…). */
  error_code?: string
  /**
   * The connection the failed round trip was started with, as passed to
   * `signInWithOauthConnection` / `linkOauthConnection`. Absent when the
   * failure was not a round trip this browser started (or its record
   * expired).
   */
  connection?: string
  connection_id?: string
  /** The round trip was a `linkOauthConnection`, not a sign-in. */
  link?: boolean
  /** The `appState` the round trip was started with. */
  appState?: unknown
  /** The `returnTo` the round trip was started with. */
  returnTo?: string
}

/**
 * The error redirect of this page load, per `storageKey`, consume-once.
 *
 * Module scope, not instance scope: an app can hold more than one client for
 * the same storageKey on one page (the dashboard rebuilds it when its settings
 * load), and only the one that parsed the URL used to have the error — the
 * others answered `null`, or each answered with its own copy and one failure
 * was shown twice. The dashboard kept a page-level flag of its own for it, and
 * a second caller under React StrictMode still raced the first.
 *
 * Holds a promise because the round trip's record is read from storage after
 * the params are already off the URL.
 */
const pageRedirectErrors = new Map<
  string,
  { error: Promise<RedirectError>; claimed: boolean }
>()

/**
 * How long a `?code=` callback may take before the client tells the server it
 * is stuck (`POST /sdk/report`, event `callback_stalled`).
 *
 * The exchange is one round-trip; on a phone on mobile data it takes under two
 * seconds. The report exists because on 2026-09-30 an iPhone came back to the
 * Fundación Rodríguez Pascual six times with a code and not one of those page
 * loads sent `POST /oauth/token`: from the server that was pure silence,
 * indistinguishable from someone closing the tab. It only reports — it does
 * not cancel or fail the exchange, which may still finish.
 */
const CALLBACK_STALL_REPORT_MS = 15 * 1000

/**
 * The exchange's answer to a `?code=` with no stored verifier. `_initialize`
 * matches on it (by message, like the identity-linked workaround: subclassed
 * errors lose their prototype in the ES5 bundle) to keep the session instead
 * of treating the code as a failed login.
 */
const NO_VERIFIER_MESSAGE =
  'No active PKCE code verifier — the authorization flow has expired or was not started'

/** A callback page load being watched: see `_watchCallback`. */
type CallbackWatch = {
  startedAt: number
  stalled: boolean
  timer: ReturnType<typeof setTimeout>
}

/** Consume-once record of why the SDK terminated the session on its own. */
export type SignOutReason = {
  /** Stable machine code when the server sent one (e.g. `user_suspended`). */
  code?: string
  /** Human-readable message from the terminal error. */
  message: string
  /** Epoch ms when the session was terminated. */
  at: number
}

const resolveStorage = (config: FaableAuthClientConfig): SupportedStorage => {
  const { storage, cookieOptions } = config
  if (storage === 'cookie' || cookieOptions) {
    return cookieStorageAdapter(cookieOptions)
  }
  if (storage === 'localStorage' || storage === undefined) {
    return localStorageAdapter
  }
  return storage
}

/**
 * The main entry point of the SDK: an isomorphic client bound to a Faable
 * Auth tenant that drives every authentication flow.
 *
 * Prefer creating it through the {@link createClient} factory in app code.
 * Once instantiated it begins loading its session in the background, so you
 * can subscribe to {@link FaableAuthClient.onAuthStateChange | auth-state
 * changes} and trigger sign-ins right away. The most common starting points
 * are the {@link FaableAuthClient.signInWithOauthConnection | sign-in}
 * methods and {@link FaableAuthClient.getSession | getSession}.
 *
 * @category Getting started
 * @see {@link https://faable.com/docs/auth/get-started | Get Started with Faable Auth}
 */
export class FaableAuthClient extends Base {
  domainUrl: string
  tokenIssuer: string
  redirectUri: string
  scope?: string
  audience?: string
  sessionCheckExpiryDays: number

  protected initializePromise: Promise<InitializeResult> | null = null
  protected _lastInitializeResult: InitializeResult | null = null
  protected detectSessionInUrl = true
  /**
   * Last step a `?code=` callback reached, sent along with a
   * {@link FaableAuthClient._reportSdk} report so the server log says WHERE it
   * stopped. `null` when this page load is not a callback.
   */
  protected _callbackStage: string | null = null
  /**
   * First characters of the callback's `?code=`, sent with every report so the
   * server can join it with the `Generated code=` and `/oauth/token` lines of
   * the same sign-in — the IP is no key: on 2026-09-30 it changed mid-login.
   */
  protected _callbackCodePrefix: string | null = null

  protected storageKey: string

  protected clientId: string
  protected storage: SupportedStorage
  protected api: FaableAuthApi

  protected autoRefreshToken: boolean
  protected autoRefreshTicker: ReturnType<typeof setInterval> | null = null
  protected visibilityChangedCallback: (() => Promise<any>) | null = null

  protected refreshingDeferred: Deferred<CallRefreshTokenResult> | null = null

  /**
   * Cross-tab broadcaster + local subscriber registry for state changes.
   */
  protected broadcastSync: BroadcastSync
  protected _session: Session | null = null

  /**
   * Initiation flow to use when redirecting to /authorize. Always PKCE in a
   * browser — it is what a tenant enforcing OAuth 2.1 accepts, and every
   * other tenant accepts it too, so the app needs no setting to follow its
   * tenant. `config.flowType` only chooses outside a browser (implicit by
   * default). Distinct from the callback-side flow, detected from URL params.
   */
  protected flowType: AuthFlowType

  /** Attribute overrides for the "last used login method" hint cookie. */
  protected lastUsedCookie?: LastUsedCookieOptions

  protected lock: Lock

  /**
   * El `appState` del login que se está consumiendo ahora mismo.
   *
   * Existe por una razón concreta: los callbacks de `onAuthStateChange` se
   * ESPERAN, y `handleRedirectCallback()` es el propio `initialize()` en
   * vuelo, así que un listener que haga `await auth.handleRedirectCallback()`
   * dentro de `SIGNED_IN` se queda colgado para siempre. Este getter se puede
   * leer ahí de forma síncrona.
   */
  protected _appState: unknown = undefined

  /**
   * Creates a new authentication client bound to a Faable Auth tenant.
   *
   * The constructor kicks off {@link FaableAuthClient.initialize} in the
   * background; you do not need to await anything before calling other methods.
   * Prefer the {@link createClient} factory in app code — it has the same
   * effect with less ceremony.
   *
   * @param config Tenant settings. `domain` and `clientId` are required and
   *   throw synchronously when missing.
   * @example
   * ```ts
   * const auth = new FaableAuthClient({
   *   domain: '<faableauth_domain>',
   *   clientId: '<client_id>',
   *   redirectUri: window.location.origin
   * })
   * ```
   * @see {@link https://faable.com/docs/auth/get-started | Get Started with Faable Auth}
   * @see {@link https://faable.com/docs/auth/clients | Clients}
   * @category Getting started
   */
  constructor(config: FaableAuthClientConfig) {
    // `undefined` (not `false`) when unset, so BaseLog can fall back to the
    // `faable.auth.debug` localStorage flag.
    const debug = config?.debug
    super({ debug })

    this.sessionCheckExpiryDays = 1
    this.redirectUri = config?.redirectUri || ''
    if (!config?.domain) {
      throw new Error('Missing domain')
    }
    this.domainUrl = getDomain(config.domain)

    this.tokenIssuer = getTokenIssuer('', this.domainUrl)
    if (!config.clientId) {
      throw new Error('Missing clientId')
    }
    this.clientId = config.clientId
    this.audience = config.audience

    this.api = new FaableAuthApi(this.domainUrl, { debug })

    // Storage key
    const key_prefix = config?.storageKey || STORAGE_KEY
    this.storageKey = `${key_prefix}-${this.clientId}`

    this.storage = resolveStorage(config)

    this.lock = new Lock({
      lock: config.lock,
      storageKey: this.storageKey,
      debug: config.debug
    })

    this.broadcastSync = new BroadcastSync(
      isBrowser() ? this.storageKey : '',
      (msg, ...args) => this._debug(msg, ...args)
    )

    // OAuth 2.1 (§447): a browser asks for a code with PKCE even when the app
    // still says `implicit` — so the same build keeps signing in the day its
    // tenant turns `oauth21_enabled` on.
    this.flowType = isBrowser() ? 'pkce' : (config.flowType ?? 'implicit')

    this.lastUsedCookie = config.lastUsedCookie

    this.autoRefreshToken = true

    this.initialize()
  }

  /**
   * Cached session value last persisted by the client.
   *
   * This is a synchronous accessor that returns whatever the client has
   * already loaded into memory. It can lag behind storage (for example,
   * across tabs before the broadcast event arrives) and never triggers a
   * refresh. Prefer {@link FaableAuthClient.getSession} when you need an
   * authoritative value, especially on the server.
   *
   * @returns The cached {@link Session} or `null` if no user is signed in.
   * @see {@link https://faable.com/docs/auth/oidc/userinfo | UserInfo}
   * @category Sessions
   */
  get session() {
    return this._session
  }

  /**
   * Initializes the client session either from the URL or from storage.
   *
   * Automatically called once from the constructor and idempotent — extra
   * calls return the same in-flight promise. Call it explicitly when you
   * need to await an OAuth, magic link, or password-recovery redirect to
   * finish processing so you can surface any returned error.
   *
   * @returns A promise that resolves to `{ error }` — non-null when the URL
   *   carried a failure or storage was corrupt; never throws.
   * @example
   * ```ts
   * const { error } = await auth.initialize()
   * if (error) console.error('Auth redirect failed', error)
   * ```
   * @see {@link https://faable.com/docs/auth/oauth-flows/authorization-code | Authorization Code with PKCE}
   * @category Lifecycle
   */
  async initialize(): Promise<InitializeResult> {
    if (this.initializePromise) {
      return await this.initializePromise
    }

    const watch = this._watchCallback()

    this.initializePromise = (async () => {
      let result: InitializeResult | undefined
      try {
        result = await this.lock._acquireLock(-1, async () => {
          this._setCallbackStage('lock_acquired')
          return await this._initialize()
        })
        return result
      } finally {
        if (watch) this._endCallbackWatch(watch, result)
      }
    })()

    const result = await this.initializePromise
    this._lastInitializeResult = result
    return result
  }

  /**
   * On a `?code=` callback, arms the stall report and marks the first stage.
   * Returns the timer so {@link FaableAuthClient.initialize} can disarm it, or
   * `null` when this page load is not a callback.
   *
   * Armed in `initialize()` and not in `_initialize()` on purpose: a callback
   * stuck waiting for the lock never reaches `_initialize()`, and that is one
   * of the places it can hang.
   */
  private _watchCallback(): CallbackWatch | null {
    if (!isBrowser() || !this.detectSessionInUrl) return null
    let code: string | undefined
    try {
      code = parseParametersFromURL(window?.location.href).code
    } catch {
      return null
    }
    if (!code) return null

    // Only a code that looks like ours travels: the server accepts hex and
    // dashes and nothing else, and a report must never be rejected for it.
    const prefix = code.slice(0, 8).toLowerCase()
    this._callbackCodePrefix = /^[0-9a-f-]{1,8}$/.test(prefix) ? prefix : null

    const watch: CallbackWatch = {
      startedAt: Date.now(),
      stalled: false,
      timer: setTimeout(() => {
        watch.stalled = true
        this._reportSdk('callback_stalled', {
          elapsed_ms: Date.now() - watch.startedAt
        })
      }, CALLBACK_STALL_REPORT_MS)
    }
    this._setCallbackStage('lock_wait')
    return watch
  }

  /**
   * Disarms the stall report. If it already fired, the callback DID finish in
   * the end — slowly — and that is reported too (`callback_recovered`, with
   * the total time): the slowness that resolves itself is exactly what left no
   * trace before, since the person eventually gets in and nobody complains.
   */
  private _endCallbackWatch(
    watch: CallbackWatch,
    result: InitializeResult | undefined
  ) {
    clearTimeout(watch.timer)
    if (!watch.stalled) return
    this._setCallbackStage(result && !result.error ? 'done' : 'failed')
    this._reportSdk('callback_recovered', {
      elapsed_ms: Date.now() - watch.startedAt
    })
  }

  private _setCallbackStage(stage: string) {
    // Only a callback page load tracks stages; the exchange helpers also run
    // for `exchangeCodeForSession()` called by hand, which is not watched.
    if (this._callbackStage === null && stage !== 'lock_wait') return
    this._callbackStage = stage
    this._debug('#_callbackStage', stage)
  }

  /**
   * Tells the auth server that a sign-in could not be completed in this
   * browser (`POST /sdk/report`). Fire-and-forget: it never throws, never
   * blocks the caller and its outcome is ignored — the one thing it must not
   * do is become a second way for the callback to fail.
   *
   * `keepalive` so it still leaves if the person gives up and navigates away,
   * which is exactly what someone looking at a stuck spinner does.
   */
  private _reportSdk(
    event: 'callback_stalled' | 'callback_no_verifier' | 'callback_recovered',
    extra: { elapsed_ms?: number; detail?: string } = {}
  ) {
    try {
      const body: Record<string, unknown> = {
        client_id: this.clientId,
        event,
        ...extra
      }
      if (this._callbackStage) body.stage = this._callbackStage
      if (this._callbackCodePrefix) body.code_prefix = this._callbackCodePrefix
      this._debug('#_reportSdk', event, body)
      void fetch(`${this.domainUrl}/sdk/report`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'x-faable-client': `auth-js/${version}+${commit}`
        },
        body: JSON.stringify(body)
      }).catch(() => {})
    } catch {
      // A report is never worth an exception.
    }
  }

  /**
   * The result of the most recent {@link FaableAuthClient.initialize} run, or
   * `null` while the first one is still in flight.
   *
   * The constructor starts `initialize()` in the background and discards the
   * promise; this accessor lets code that cannot await it (for example a
   * React provider effect) read whether the last OAuth/redirect attempt
   * errored, instead of being stuck with a session that silently stays
   * `null`.
   *
   * @category Lifecycle
   */
  get lastInitializeResult(): InitializeResult | null {
    return this._lastInitializeResult
  }

  /**
   * Lo que la app pidió llevarse al arrancar el login ({@link SignInWithOAuthConnection.appState}),
   * ya deserializado, o `undefined` si no pidió nada.
   *
   * Es la misma información que `appState` en el resultado de
   * {@link FaableAuthClient.handleRedirectCallback}, pero **legible de forma
   * síncrona**, que es lo que hace falta dentro de un
   * {@link FaableAuthClient.onAuthStateChange}:
   *
   * ```ts
   * auth.onAuthStateChange((ev) => {
   *   if (ev === 'SIGNED_IN') console.log(auth.appState)
   * })
   * ```
   *
   * ⚠️ **No llames a `handleRedirectCallback()` dentro de un listener**: los
   * callbacks se esperan y ese método es el `initialize()` que los está
   * emitiendo, así que la promesa no resuelve nunca.
   */
  get appState(): unknown {
    return this._appState
  }

  /**
   * Completes an OAuth / magic-link / password-recovery redirect on your
   * callback route and reports the outcome.
   *
   * The SDK already consumes the URL during the `initialize()` it kicks off
   * from the constructor; this is a thin, discoverable wrapper that awaits
   * that same in-flight run (idempotent) so you can:
   *
   * - redirect **only once** the token exchange has finished, and
   * - surface `error` instead of hanging on a "Signing you in…" screen when
   *   the exchange fails (e.g. an expired PKCE verifier).
   *
   * It also returns `returnTo` — the app-side destination you optionally
   * passed to `signInWith*({ returnTo })` — so you don't need a side channel
   * (like `sessionStorage`) to remember where to send the user.
   *
   * @example
   * ```ts
   * // app/callback/page.tsx
   * const { error, returnTo } = await auth.handleRedirectCallback()
   * if (error) showError(error.message)
   * else router.replace(returnTo ?? '/')
   * ```
   * @see {@link FaableAuthClient.initialize}
   * @category Lifecycle
   */
  async handleRedirectCallback(): Promise<InitializeResult> {
    return await this.initialize()
  }

  /**
   * IMPORTANT:
   * 1. Never throw in this method, as it is called from the constructor
   * 2. Never return a session from this method as it would be cached over
   *    the whole lifetime of the client
   */
  private async _initialize(): Promise<InitializeResult> {
    try {
      const flow = await this._detectFlowType()

      this._debug('#_initialize()', 'begin', 'flow_type', flow)

      // if exists any flow, process the session
      if (flow) {
        const { data, error } = await this._getSessionFromURL(flow)
        if (error) {
          this._debug(
            '#_initialize()',
            'error detecting session from URL',
            error
          )

          // hacky workaround to keep the existing session if there's an error returned from identity linking
          // TODO: once error codes are ready, we should match against it instead of the message
          if (
            error?.message === 'Identity is already linked' ||
            error?.message === 'Identity is already linked to another user'
          ) {
            return { error }
          }

          // A server-returned error redirect (`?error=...`) is a failed
          // ATTEMPT, not a corrupted session. The user may be signed in and
          // merely failed a connect/link round-trip (2026-08-21: a GitHub
          // connect denied by the network gate); nuking their session here
          // turned a failed connect into a logout. `details` is only set on
          // the error-params throw — the missing-token variants keep the
          // legacy removal below.
          //
          // Matched by `name`, NOT by instanceof: the published CJS bundle is
          // ES5, where subclassing Error breaks the prototype chain — at
          // runtime `constructor.name` is 'Error' and instanceof is false
          // even for an error this very bundle constructed (v2.3.0 shipped
          // the instanceof form and kept logging users out). Same reason the
          // identity-linked workaround above matches on `message`.
          if (
            error?.name === 'AuthImplicitGrantRedirectError' &&
            (error as AuthImplicitGrantRedirectError).details
          ) {
            // This early return skips the storage recovery at the bottom of
            // the flow — load the persisted session into memory here so the
            // signed-in user still LOOKS signed in for this page load.
            await this._recoverAndRefresh()
            return { error }
          }

          // The URL no longer carries any auth-flow artifacts: whatever we
          // just failed to process was already consumed — typically by a
          // second client instance on the same page that stripped the params
          // between our flow detection and the parse (both straddle an
          // await). That is a stale read, not a failed login; removing the
          // session here logged the user out for a URL that no longer says
          // anything. Recover storage like a normal page load instead.
          if ((await this._detectFlowType()) === null) {
            await this._recoverAndRefresh()
            return { error }
          }

          // A code this client holds no verifier for was never a login of
          // ours: nothing was exchanged, so there is no failed attempt to
          // clean up after. It is a URL replayed after its verifier was spent
          // — the browser re-requesting a used `/authorize` and auth answering
          // by silent SSO with a fresh code. Removing the session here turned
          // that into a forced re-login, seven times in 14 minutes for one
          // signed-in user (2026-10-05). Drop the code so nothing downstream
          // reads it as an exchange in flight, and keep what is stored.
          if (
            error?.name === 'AuthPKCEGrantCodeExchangeError' &&
            error.message === NO_VERIFIER_MESSAGE
          ) {
            clearURLParameters([...CALLBACK_MARKERS])
            await this._recoverAndRefresh()
            return { error }
          }

          // failed login attempt via url,
          // remove old session as in verifyOtp, signUp and signInWith*
          //
          // An authorization code is single-use: whatever made this exchange
          // fail (expired, already redeemed, a network error), it will never
          // work again. Left in the URL, a reload replays the same failure and
          // a retry button lands on it. Same strip as the no-verifier case
          // above; `state` is left alone, it may be the app's.
          if (flow === 'pkce') clearURLParameters([...CALLBACK_MARKERS])
          await this._removeSession()

          return { error }
        }

        const { session, redirectType, returnTo, is_new_user, appState } = data

        this._debug(
          '#_initialize()',
          'detected session in URL',
          session,
          'redirect type',
          redirectType
        )

        await this._saveSession(session)
        // Signed in by another route (a magic link, a social button pressed
        // instead of typing the code): the code is not coming back either.
        await this.clearPendingPasswordless()

        setTimeout(async () => {
          if (redirectType === 'recovery') {
            await this._notifyAllSubscribers('PASSWORD_RECOVERY', session)
          } else {
            await this._notifyAllSubscribers('SIGNED_IN', session)
          }
        }, 0)

        return { error: null, redirectType, returnTo, is_new_user, appState }
      }
      // no login attempt via callback url try to recover session from storage
      await this._recoverAndRefresh()
      return { error: null }
    } catch (error) {
      if (isAuthError(error)) {
        return { error }
      }

      return {
        error: new AuthUnknownError(
          'Unexpected error during initialization',
          error
        )
      }
    } finally {
      await this._handleVisibilityChange()
      this._debug('#_initialize()', 'end')
    }
  }

  /**
   * Gets the session data from a URL string
   */
  private async _getSessionFromURL(flow: AuthFlowType): Promise<
    | {
        data: {
          session: Session
          redirectType: string | null
          returnTo: string | null
          is_new_user: boolean
          appState?: unknown
        }
        error: null
      }
    | {
        data: {
          session: null
          redirectType: null
          returnTo: null
          is_new_user: false
          appState?: unknown
        }
        error: AuthError
      }
  > {
    try {
      const params = parseParametersFromURL(window?.location.href)
      // The auth server appends `?signup=true` to the callback when the login
      // just created a new account (social/OAuth only). Surface it so the app
      // can branch into onboarding; strip it from the URL alongside the tokens.
      const is_new_user = params.signup === 'true'
      if (flow == 'pkce') {
        if (!params.code) {
          throw new AuthPKCEGrantCodeExchangeError('No code detected.')
        }

        const { data, error } = await this._exchangeCodeForSession(params.code)
        if (error) throw error

        // Remove code (and the signup marker) from URL — plus a `state` this
        // client never sent, which can only have come from the server.
        clearURLParameters(callbackParamsToClear({ sentState: data.sentState }))

        await this._promoteLoginAttempt()

        return {
          data: {
            session: data.session,
            redirectType: data.redirectType,
            returnTo: data.returnTo,
            is_new_user,
            appState: data.appState
          },
          error: null
        }
      }

      if (params.error || params.error_description || params.error_code) {
        // Server-returned error redirect (RFC 6749 §4.1.2.1): stash it for a
        // one-shot getRedirectError() read and strip the params so a reload
        // doesn't replay the failure. The throw below surfaces it to
        // _initialize, which deliberately KEEPS any existing session — this
        // redirect may come from a connect/link attempt by a signed-in user.
        // Stashed before the strip, in the same tick as the parse: no other
        // client on this page can read these params after it.
        pageRedirectErrors.set(this.storageKey, {
          error: this._redirectErrorWithAttempt({
            error: params.error || 'unspecified_error',
            error_description:
              params.error_description ||
              'Error in URL with unspecified error_description',
            ...(params.error_code ? { error_code: params.error_code } : {})
          }),
          claimed: false
        })
        clearURLParameters(['error', 'error_description', 'error_code', 'iss'])
        throw new AuthImplicitGrantRedirectError(
          params.error_description ||
            'Error in URL with unspecified error_description',
          {
            error: params.error || 'unspecified_error',
            code: params.error_code || 'unspecified_code'
          }
        )
      }

      const {
        provider_token,
        provider_refresh_token,
        access_token,
        refresh_token,
        expires_in,
        expires_at,
        token_type
      } = params

      // We only strictly need the token pair. Some flows hand the tokens back
      // in the query string without the full OIDC envelope (`expires_in` /
      // `token_type`); fall back to the access token's own `exp` claim and a
      // `bearer` default instead of rejecting an otherwise-valid callback.
      if (!access_token || !refresh_token) {
        throw new AuthImplicitGrantRedirectError('No session defined in URL')
      }

      let expiresAt: number
      let expiresIn: number
      if (expires_in) {
        // Check time is valid
        ;({ expiresAt, expiresIn } = checkExpiresInTime({
          expires_in,
          expires_at,
          refreshTick: AUTO_REFRESH_TICK_DURATION
        }))
      } else {
        ;({ expiresAt, expiresIn } = expiryFromAccessToken(
          access_token,
          expires_at
        ))
      }

      const { data: user, error } = await this._getUser(access_token)

      if (error || !user) throw error

      const session: Session = {
        provider_token,
        provider_refresh_token,
        access_token,
        expires_in: expiresIn,
        expires_at: expiresAt,
        refresh_token,
        token_type: token_type || 'bearer',
        user
      }

      // Remove tokens from URL
      clearURLParameters([
        'access_token',
        'expires_in',
        'refresh_token',
        'token_type',
        'scope',
        'signup',
        'iss'
      ])
      this._debug('#_getSessionFromURL()', 'clearing window.location.hash')

      await this._promoteLoginAttempt()

      return {
        data: {
          session,
          redirectType: params.type,
          returnTo: null,
          is_new_user
        },
        error: null
      }
    } catch (error) {
      this._debug(error)
      if (isAuthError(error)) {
        return {
          data: {
            session: null,
            redirectType: null,
            returnTo: null,
            is_new_user: false
          },
          error
        }
      }
      throw error
    }
  }

  private async _exchangeCodeForSession(authCode: string): Promise<
    | {
        data: {
          session: Session
          user: User
          redirectType: string | null
          returnTo: string | null
          sentState: boolean
          appState?: unknown
        }
        error: null
      }
    | {
        data: {
          session: null
          user: null
          redirectType: null
          returnTo: null
          sentState: boolean
          appState?: unknown
        }
        error: AuthError
      }
  > {
    this._setCallbackStage('verifier_loading')
    const stored = await loadCodeVerifier(
      this.storage,
      `${this.storageKey}-code-verifier`
    )
    if (!stored) {
      this._reportSdk('callback_no_verifier')
      return {
        data: {
          user: null,
          session: null,
          redirectType: null,
          returnTo: null,
          // Nothing is known about this flow, so assume the `state` in the URL
          // is the app's and leave it alone.
          sentState: true
        },
        error: new AuthPKCEGrantCodeExchangeError(NO_VERIFIER_MESSAGE)
      }
    }
    const {
      verifier: codeVerifier,
      redirectType = null,
      returnTo = null,
      sentState = false,
      appState,
      redirectUri
    } = stored

    // Antes del canje, y por tanto antes del SIGNED_IN que se emite dentro:
    // un listener tiene que poder leerlo mientras reacciona.
    this._appState = appState

    this._setCallbackStage('token_request_sent')
    const rawResponse = await _post<Partial<RawAuthResponse>>(
      `${this.domainUrl}/oauth/token`,
      {
        client_id: this.clientId,
        grant_type: 'authorization_code',
        code: authCode,
        code_verifier: codeVerifier,
        // Repeat the EXACT redirect_uri sent to /authorize (RFC 6749 §4.1.3,
        // §305). Older stored flows have none — then it is omitted, as before.
        ...(redirectUri ? { redirect_uri: redirectUri } : {}),
        ...(this.audience ? { audience: this.audience } : {})
      }
    )
    this._setCallbackStage('token_response')

    const { data, error } = _sessionResponse(rawResponse)

    if (!data) {
      throw new Error('Missing data')
    }

    await this.storage.removeItem(`${this.storageKey}-code-verifier`)

    if (error) {
      return {
        data: {
          user: null,
          session: null,
          redirectType: null,
          returnTo: null,
          sentState,
          appState
        },
        error
      }
    } else if (!data || !data.session || !data.user) {
      return {
        data: {
          user: null,
          session: null,
          redirectType: null,
          returnTo: null,
          sentState,
          appState
        },
        error: new AuthInvalidTokenResponseError()
      }
    }
    let session = data.session as Session
    if (session) {
      const { data: user, error } = await this._getUser(session.access_token)
      if (error || !user) {
        throw error
      }

      session = {
        ...session,
        user
      }
      data.session = session

      await this._saveSession(session)
      await this._notifyAllSubscribers('SIGNED_IN', session)
    }
    return {
      data: {
        ...data,
        redirectType: redirectType ?? null,
        returnTo: returnTo ?? null,
        // ⚠️ Not optional here: this object is cast `as any`, so forgetting it
        // makes `sentState` undefined at the call site — which reads as "the
        // app sent no state" and wipes a `state` that IS the app's.
        sentState,
        appState
      } as any,
      error
    }
  }

  /**
   * Registers callbacks on the browser / platform, which in-turn run
   * algorithms when the browser window/tab are in foreground. On non-browser
   * platforms it assumes always foreground.
   */
  private async _handleVisibilityChange() {
    this._debug('#_handleVisibilityChange()')

    if (!isBrowser() || !window?.addEventListener) {
      if (this.autoRefreshToken) {
        // in non-browser environments the refresh token ticker runs always
        this.startAutoRefresh()
      }

      return false
    }

    try {
      this.visibilityChangedCallback = async () =>
        await this._onVisibilityChanged(false)

      window?.addEventListener(
        'visibilitychange',
        this.visibilityChangedCallback
      )

      // now immediately call the visbility changed callback to setup with the
      // current visbility state
      await this._onVisibilityChanged(true) // initial call
    } catch (error) {
      console.error('_handleVisibilityChange', error)
    }
  }

  /**
   * Callback registered with `window.addEventListener('visibilitychange')`.
   */
  private async _onVisibilityChanged(calledFromInitialize: boolean) {
    const methodName = `#_onVisibilityChanged(${calledFromInitialize})`
    this._debug(methodName, 'visibilityState', document.visibilityState)

    if (document.visibilityState === 'visible') {
      if (this.autoRefreshToken) {
        // in browser environments the refresh token ticker runs only on focused tabs
        // which prevents race conditions
        this._startAutoRefresh()
      }

      if (!calledFromInitialize) {
        // called when the visibility has changed, i.e. the browser
        // transitioned from hidden -> visible so we need to see if the session
        // should be recovered immediately... but to do that we need to acquire
        // the lock first asynchronously
        await this.initializePromise

        await this.lock._acquireLock(-1, async () => {
          if (document.visibilityState !== 'visible') {
            this._debug(
              methodName,
              'acquired the lock to recover the session, but the browser visibilityState is no longer visible, aborting'
            )

            // visibility has changed while waiting for the lock, abort
            return
          }

          // recover the session
          await this._recoverAndRefresh()
        })
      }
    } else if (document.visibilityState === 'hidden') {
      if (this.autoRefreshToken) {
        this._stopAutoRefresh()
      }
    }
  }

  /**
   * Recovers the session from LocalStorage and refreshes
   * Note: this method is async to accommodate for AsyncStorage e.g. in React native.
   */
  private async _recoverAndRefresh() {
    const debugName = '#_recoverAndRefresh()'
    this._debug(debugName, 'begin')

    try {
      const currentSession = await getItemAsync(this.storage, this.storageKey)
      this._debug(debugName, 'session from storage', currentSession)

      if (!isValidSession(currentSession)) {
        this._debug(debugName, 'session is not valid')
        if (currentSession !== null) {
          await this._removeSession()
        }

        return
      }

      const timeNow = Math.round(Date.now() / 1000)
      const expiresWithMargin =
        (currentSession.expires_at ?? Infinity) < timeNow + EXPIRY_MARGIN

      this._debug(
        debugName,
        `session has${
          expiresWithMargin ? '' : ' not'
        } expired with margin of ${EXPIRY_MARGIN}s`
      )

      if (expiresWithMargin) {
        if (this.autoRefreshToken && currentSession.refresh_token) {
          const { error } = await this._callRefreshToken(
            currentSession.refresh_token
          )

          if (error) {
            console.error(error)

            if (!isAuthRetryableFetchError(error)) {
              this._debug(
                debugName,
                'refresh failed with a non-retryable error, removing the session',
                error
              )
              await this._removeSession()
            }
          }
        }
      } else {
        // no need to persist currentSession again, as we just loaded it from
        // local storage; persisting it again may overwrite a value saved by
        // another client with access to the same local storage
        this._session = currentSession
        await this._notifyAllSubscribers('SIGNED_IN', currentSession)
      }
    } catch (err) {
      this._debug(debugName, 'error', err)

      console.error(err)
      return
    } finally {
      this._debug(debugName, 'end')
    }
  }

  /**
   * Removes any registered visibilitychange callback.
   *
   * @see startAutoRefresh
   * @see stopAutoRefresh
   */
  private _removeVisibilityChangedCallback() {
    this._debug('#_removeVisibilityChangedCallback()')

    const callback = this.visibilityChangedCallback
    this.visibilityChangedCallback = null

    try {
      if (callback && isBrowser() && window?.removeEventListener) {
        window.removeEventListener('visibilitychange', callback)
      }
    } catch (e) {
      console.error('removing visibilitychange callback failed', e)
    }
  }

  /**
   * Starts an auto-refresh process in the background. The session is checked
   * every few seconds. Close to the time of expiration a process is started to
   * refresh the session. If refreshing fails it will be retried for as long as
   * necessary.
   *
   * If `autoRefreshToken` is enabled in the client config you don't need to
   * call this function, it will be called for you.
   *
   * On browsers the refresh process works only when the tab/window is in the
   * foreground to conserve resources as well as prevent race conditions and
   * flooding auth with requests. If you call this method any managed
   * visibility change callback will be removed and you must manage visibility
   * changes on your own.
   *
   * On non-browser platforms the refresh process works *continuously* in the
   * background, which may not be desirable. You should hook into your
   * platform's foreground indication mechanism and call these methods
   * appropriately to conserve resources.
   *
   * @example
   * ```ts
   * // React Native / Node: drive the refresh loop on focus events yourself
   * appState.addEventListener('change', state => {
   *   if (state === 'active') auth.startAutoRefresh()
   * })
   * ```
   * @see {@link https://faable.com/docs/auth/oauth-flows/refresh-token | Refresh Token}
   * @category Sessions
   */
  async startAutoRefresh() {
    this._removeVisibilityChangedCallback()
    await this._startAutoRefresh()
  }

  /**
   * This is the private implementation of {@link #startAutoRefresh}. Use this
   * within the library.
   */
  private async _startAutoRefresh() {
    await this._stopAutoRefresh()

    this._debug('#_startAutoRefresh()')

    const ticker = setInterval(
      () => this._autoRefreshTokenTick(),
      AUTO_REFRESH_TICK_DURATION
    )
    this.autoRefreshTicker = ticker

    if (
      ticker &&
      typeof ticker === 'object' &&
      typeof ticker.unref === 'function'
    ) {
      // ticker is a NodeJS Timeout object that has an `unref` method
      // https://nodejs.org/api/timers.html#timeoutunref
      // When auto refresh is used in NodeJS (like for testing) the
      // `setInterval` is preventing the process from being marked as
      // finished and tests run endlessly. This can be prevented by calling
      // `unref()` on the returned object.
      ticker.unref()
    } else if (
      typeof (globalThis as any).Deno !== 'undefined' &&
      typeof (globalThis as any).Deno.unrefTimer === 'function'
    ) {
      // Same intent as Node's unref(), via the Deno API.
      // https://deno.land/api@latest?unstable&s=Deno.unrefTimer
      ;(globalThis as any).Deno.unrefTimer(ticker)
    }

    // run the tick immediately, but in the next pass of the event loop so that
    // #_initialize can be allowed to complete without recursively waiting on
    // itself
    setTimeout(async () => {
      await this.initializePromise
      await this._autoRefreshTokenTick()
    }, 0)
  }

  /**
   * This is the private implementation of {@link #stopAutoRefresh}. Use this
   * within the library.
   */
  private async _stopAutoRefresh() {
    this._debug('#_stopAutoRefresh()')

    const ticker = this.autoRefreshTicker
    this.autoRefreshTicker = null

    if (ticker) {
      clearInterval(ticker)
    }
  }

  /**
   * Runs the auto refresh token tick.
   */
  private async _autoRefreshTokenTick() {
    this._debug('#_autoRefreshTokenTick()', 'begin')

    try {
      await this.lock._acquireLock(0, async () => {
        try {
          const now = Date.now()

          try {
            return await this._useSession(async result => {
              const {
                data: { session }
              } = result

              if (!session || !session.refresh_token || !session.expires_at) {
                this._debug('#_autoRefreshTokenTick()', 'no session')
                return
              }

              // session will expire in this many ticks (or has already expired if <= 0)
              const expiresInTicks = Math.floor(
                (session.expires_at * 1000 - now) / AUTO_REFRESH_TICK_DURATION
              )

              this._debug(
                '#_autoRefreshTokenTick()',
                `access token expires in ${expiresInTicks} ticks, a tick lasts ${AUTO_REFRESH_TICK_DURATION}ms, refresh threshold is ${AUTO_REFRESH_TICK_THRESHOLD} ticks`
              )

              if (expiresInTicks <= AUTO_REFRESH_TICK_THRESHOLD) {
                await this._callRefreshToken(session.refresh_token)
              }
            })
          } catch (e: any) {
            console.error(
              'Auto refresh tick failed with error. This is likely a transient error.',
              e
            )
          }
        } finally {
          this._debug('#_autoRefreshTokenTick()', 'end')
        }
      })
    } catch (e: any) {
      if (e.isAcquireTimeout || e instanceof LockAcquireTimeoutError) {
        this._debug('auto refresh token tick lock not available')
      } else {
        throw e
      }
    }
  }

  private async _detectFlowType(): Promise<AuthFlowType | null> {
    const params = parseParametersFromURL(window?.location.href)

    const browser = isBrowser()

    // PKCE
    if (browser && params.code) {
      return 'pkce'
    }

    // Implicit
    if (browser && (params.access_token || params.error_description)) {
      return 'implicit'
    }
    return null
  }

  private _scope() {
    return this.scope || 'openid profile email'
  }

  private async _getUrlForConnection(
    url: string,
    params: {
      connection?: string
      connection_id?: string
      redirectTo?: string
      returnTo?: string
      scopes?: string
      response_type?: 'code' | 'token'
      queryParams?: { [key: string]: string }
      skipBrowserRedirect?: boolean
      audience?: string
      appState?: unknown
      link?: boolean
    }
  ) {
    let urlParams: Record<string, any> = params.queryParams || {}

    // Resolved ONCE and reused below so the exact same string is persisted for
    // the token exchange to replay (RFC 6749 §4.1.3, §305).
    const redirectUri =
      params.redirectTo || this.redirectUri || window?.location.origin

    const authorize_params: Record<string, any> = {
      client_id: this.clientId,
      response_type: params.response_type,
      redirect_uri: redirectUri,
      scope: params.scopes || this._scope()
    }

    if (this.flowType === 'pkce') {
      // Did the APP put a `state` in this authorize URL? Remembered alongside
      // the verifier, because on the way back it is the only way to tell the
      // app's own `state` (keep it: it is theirs to read) from one the server
      // added (wipe it). See `callbackParamsToClear`.
      const sentState = urlParams.state !== undefined
      const [codeChallenge, codeChallengeMethod] =
        await getCodeChallengeAndMethod(
          this.storage,
          this.storageKey,
          false,
          params.returnTo,
          {
            sentState,
            appState: params.appState,
            redirectUri,
            connection: params.connection,
            connectionId: params.connection_id,
            link: params.link
          }
        )

      urlParams = {
        ...urlParams,
        code_challenge: codeChallenge,
        code_challenge_method: codeChallengeMethod
      }
    }

    if (params.connection_id) {
      authorize_params.connection_id = params.connection_id
    } else if (params.connection) {
      authorize_params.connection = params.connection
    }

    const audience = params.audience ?? this.audience
    if (audience) {
      authorize_params.audience = audience
    }

    return `${url}?${new URLSearchParams({
      ...urlParams,
      ...authorize_params
    })}`
  }

  /**
   * Starts an OAuth / social login by redirecting the browser to the tenant's
   * `/authorize` endpoint for the chosen connection.
   *
   * In browsers the SDK redirects the current window unless
   * `skipBrowserRedirect` is `true`. On that redirect success path the returned
   * promise **never resolves** — the pending navigation owns the page, so a
   * loading state you tie to the `await` stays on until unload instead of
   * flashing back. Pass `skipBrowserRedirect: true` to get `{ data: { url } }`
   * back and drive the navigation yourself. PKCE is used by default in
   * browsers, falling back to the implicit flow elsewhere. Prefer
   * `connection_id` when known — the backend resolves it without an extra
   * lookup; `connection` (by name) is kept for legacy tenants.
   *
   * @example
   * ```ts
   * // The tenant's hosted login, with every connection the app offers. With a
   * // live SSO session it signs the user in without showing anything.
   * await auth.signInWithOauthConnection()
   *
   * // Redirects the current window; the promise does not resolve on success.
   * await auth.signInWithOauthConnection({
   *   connection: 'google',
   *   redirectTo: 'https://app.example.com/callback'
   * })
   *
   * // Or take over the navigation yourself:
   * const { data } = await auth.signInWithOauthConnection({
   *   connection: 'google',
   *   skipBrowserRedirect: true
   * })
   * window.location.assign(data.url)
   * ```
   * @see {@link https://faable.com/docs/auth/connections | Connections}
   * @see {@link https://faable.com/docs/auth/oauth-flows/authorization-code | Authorization Code with PKCE}
   * @category Sign in
   */
  async signInWithOauthConnection(
    credentials: SignInWithOAuthConnection = {}
  ): Promise<OAuthResponse> {
    return await this._handleConnectionSignIn({
      connection: credentials.connection,
      connection_id: credentials.connection_id,
      redirectTo: credentials?.redirectTo,
      returnTo: credentials?.returnTo,
      scopes: credentials?.scopes,
      queryParams: credentials.queryParams,
      skipBrowserRedirect: credentials.skipBrowserRedirect,
      audience: credentials.audience,
      appState: credentials.appState
    })
  }

  /**
   * Sends the user to the tenant's hosted login, with every connection the
   * application offers. With a live SSO session (the user signed in to
   * another app of the tenant) it comes back signed in without showing
   * anything. Same as {@link signInWithOauthConnection}, which also takes a
   * specific connection.
   *
   * @example
   * ```ts
   * await auth.signIn()
   * await auth.signIn({ returnTo: '/settings' })
   * ```
   * @see {@link https://faable.com/docs/auth/hosted-login | Hosted login}
   * @category Sign in
   */
  async signIn(
    options: SignInWithOAuthConnection = {}
  ): Promise<OAuthResponse> {
    return this.signInWithOauthConnection(options)
  }

  /**
   * Links an additional OAuth identity (e.g. GitHub) to the CURRENTLY
   * signed-in user — the identity round-trip proves control of the provider
   * account and attaches it to the session user; it never signs in, never
   * creates a user, and never touches the user's email or profile.
   *
   * This is NOT {@link signInWithOauthConnection}: a sign-in with an unknown
   * provider identity creates a brand-new user, which is exactly the
   * duplicate-account hole "connect" buttons used to fall into. Requires an
   * active session (the server returns `error=login_required` otherwise) and
   * must not be combined with `prompt: 'login'` (it would destroy the
   * session being linked to).
   *
   * On failure the server redirects back with `?error=...` — read it with
   * {@link getRedirectError} after the round-trip.
   *
   * @example
   * ```ts
   * await auth.linkOauthConnection({
   *   connection_id: GITHUB_CONNECTION_ID,
   *   redirectTo: window.location.href
   * })
   * ```
   * @see {@link https://faable.com/docs/auth/connections | Connections}
   * @category Sign in
   */
  async linkOauthConnection(
    credentials: SignInWithOAuthConnection
  ): Promise<OAuthResponse> {
    // The server links to the user of ITS cookie session — which a session
    // obtained through a direct grant (passwordless OTP against
    // /oauth/token) never created, and a cross-site cookie would be dropped
    // anyway. The ticket carries who to link to instead; /authorize
    // establishes the session from it on the top-level navigation. Without
    // it, every OTP-authenticated user got `login_required` here
    // (arch/auth/link-mode-session-ticket.md).
    const link_ticket = await this._mintLinkTicket()
    return await this._handleConnectionSignIn({
      connection: credentials.connection,
      connection_id: credentials.connection_id,
      redirectTo: credentials?.redirectTo,
      returnTo: credentials?.returnTo,
      scopes: credentials?.scopes,
      queryParams: link_ticket
        ? { ...(credentials.queryParams ?? {}), link_ticket }
        : credentials.queryParams,
      skipBrowserRedirect: credentials.skipBrowserRedirect,
      audience: credentials.audience,
      appState: credentials.appState,
      link: true
    })
  }

  /**
   * Trades the current access token for a single-use link ticket
   * (`POST /oauth/link_ticket`). Best-effort by design: no session, an
   * older server without the endpoint, an expired token or a network error
   * all resolve to `null`, and the link round-trip proceeds without a
   * ticket — the server then answers exactly as it did before tickets
   * existed (`login_required` unless it has a cookie session).
   */
  private async _mintLinkTicket(): Promise<string | null> {
    try {
      const { data } = await this.getSession()
      const access_token = data?.session?.access_token
      if (!access_token) {
        this._debug('#_mintLinkTicket()', 'no session, skipping')
        return null
      }
      const res = await _post<{ link_ticket?: string }>(
        `${this.domainUrl}/oauth/link_ticket`,
        {},
        { token: access_token }
      )
      if (res.error || !res.data?.link_ticket) {
        this._debug('#_mintLinkTicket()', 'not issued', res.status, res.error)
        return null
      }
      return res.data.link_ticket
    } catch (error) {
      this._debug('#_mintLinkTicket()', 'failed', error)
      return null
    }
  }

  /**
   * Signs the user in with a username + password against a database
   * connection on the tenant.
   *
   * The server responds with an HTML form that posts the user back to the
   * tenant's `/login/callback` to complete the OAuth flow; the SDK
   * auto-submits it from the current document. That is why the success path
   * resolves to `{ data: null, error: null }` — the actual session lands on
   * the redirect target, not on this return value. Subscribe with
   * {@link FaableAuthClient.onAuthStateChange} to observe the resulting
   * `SIGNED_IN` event.
   *
   * @example
   * ```ts
   * await auth.signInWithUsernamePassword({
   *   username: 'user@example.com',
   *   password: '••••••••',
   *   redirectTo: 'https://app.example.com/callback'
   * })
   * ```
   * @see {@link https://faable.com/docs/auth/connections | Connections}
   * @category Sign in
   */
  async signInWithUsernamePassword(data: {
    username: string
    password: string
    redirectTo?: string
    state?: string
    audience?: string
  }): Promise<AuthResult<null>> {
    const audience = data.audience ?? this.audience
    const redirectUri =
      data.redirectTo || this.redirectUri || window?.location.origin
    // OAuth 2.1: a code bound by PKCE, not tokens in the redirect URL. Only
    // for a login of our own — with a `state` the login continues an
    // `/authorize` that already set its challenge, and its verifier is the
    // one stored; minting another here would overwrite it.
    const pkce =
      this.flowType === 'pkce' && !data.state
        ? await getCodeChallengeAndMethod(
            this.storage,
            this.storageKey,
            false,
            undefined,
            { redirectUri }
          )
        : undefined
    const rawAuthResponse = await _post<string>(
      `${this.domainUrl}/usernamepassword/login`,
      {
        username: data.username,
        password: data.password,
        redirect_uri: redirectUri,
        client_id: this.clientId,
        state: data.state,
        // `plain` (no WebCrypto: an insecure context) is refused by the
        // server; such a page keeps the old token response.
        ...(pkce && pkce[1] === 'S256'
          ? {
              response_type: 'code',
              code_challenge: pkce[0],
              code_challenge_method: pkce[1]
            }
          : {}),
        ...(audience ? { audience } : {})
      },
      { raw: true }
    )

    if (!rawAuthResponse.data || rawAuthResponse.error) {
      const raw = rawAuthResponse.error
      const message =
        (raw instanceof Error ? raw.message : raw) ||
        'Error in username password login'
      return {
        data: null,
        // When the server answered, surface a typed error carrying its
        // stable machine code (e.g. `user_suspended`) so login UIs can
        // branch on `error.code`; without a status there was no verdict
        // (network failure) and the legacy unknown-error shape stands.
        error:
          rawAuthResponse.status !== undefined
            ? new AuthApiError(
                message,
                rawAuthResponse.status,
                rawAuthResponse.code
              )
            : new AuthUnknownError(message, raw)
      }
    }
    // The form below navigates the page; record the attempt while we still can.
    await this._saveLoginAttempt({ method: 'password' })
    buildAndSubmitForm(rawAuthResponse.data, document)
    return { data: null, error: null }
  }

  /**
   * Creates a user in the tenant's database connection, and nothing else: no
   * login, no navigation. The browser-side counterpart of Auth0's
   * `webAuth.signup()` — a contact or waitlist form can register people
   * without a backend of its own.
   *
   * Calls the public `POST /dbconnections/signup`. Which identifier is required
   * follows the connection's login identifier (email, username or either), and
   * the password is optional: a user created without one sets it through the
   * password reset email. Unlike Auth0, which rejects a signup without password,
   * Faable neither generates one nor stores an empty one — the credential just
   * has no password yet.
   *
   * The user is created with `email_verified: false`; any verification /
   * welcome email is driven by the tenant's account settings.
   *
   * @param data The new user's identifier(s), optional password and profile fields.
   * @example
   * ```ts
   * const { data, error } = await auth.signup({
   *   email: 'user@example.com',
   *   name: 'Ada Lovelace',
   *   user_metadata: { source: 'contact-form' }
   * })
   * if (error?.code === 'email_exists') showAlreadyRegistered()
   * else if (error) showError(error.message)
   * else console.log('created', data.user_id)
   * ```
   * @see {@link https://faable.com/docs/auth/connections | Connections}
   * @category Sign in
   */
  async signup(data: SignupParams): Promise<AuthResult<SignupResult>> {
    if (!data?.email && !data?.username) {
      return {
        data: null,
        error: new AuthUnknownError('email or username is required', null)
      }
    }

    const {
      data: body,
      error,
      status,
      code
    } = await _post<SignupResult>(`${this.domainUrl}/dbconnections/signup`, {
      client_id: this.clientId,
      ...(data.email ? { email: data.email } : {}),
      ...(data.username ? { username: data.username } : {}),
      ...(data.password !== undefined ? { password: data.password } : {}),
      ...(data.name ? { name: data.name } : {}),
      ...(data.given_name ? { given_name: data.given_name } : {}),
      ...(data.family_name ? { family_name: data.family_name } : {}),
      ...(data.user_metadata ? { user_metadata: data.user_metadata } : {}),
      ...(data.connection ? { connection: data.connection } : {})
    })

    if (error) {
      // `_post` hands back the server's `{ status, message }` body as `data`
      // and its `message` as `error`. Map the HTTP status to a stable
      // ErrorCode so callers can branch without string-matching the message.
      const httpStatus = status ?? ((body as any)?.status as number | undefined)
      const errorCode =
        httpStatus === 403
          ? 'signup_disabled'
          : httpStatus === 409
            ? code === 'username_taken'
              ? 'username_exists'
              : 'email_exists'
            : code === 'password_too_weak'
              ? 'weak_password'
              : httpStatus === 400
                ? 'validation_failed'
                : undefined
      return {
        data: null,
        error: new AuthApiError(String(error), httpStatus ?? 500, errorCode)
      }
    }

    return {
      data: {
        user_id: (body as SignupResult).user_id,
        email_verified: (body as SignupResult).email_verified
      },
      error: null
    }
  }

  /**
   * Creates a user with {@link FaableAuthClient.signup} and then signs them
   * in — the counterpart of Auth0's `signupAndLogin`.
   *
   * **The login navigates the browser.** Like every interactive
   * username/password login in this SDK, it submits a form that round-trips
   * through the auth server, so on success the page redirects to your
   * `redirectTo` and the live session is delivered there by
   * {@link FaableAuthClient.initialize} (and a `SIGNED_IN` event). This method
   * only returns when the signup or the login fails, or in non-navigating
   * runtimes (e.g. tests). The password is required here: without one there is
   * nothing to sign in with.
   *
   * @param data The new user, with its password, plus where the login lands.
   * @example
   * ```ts
   * const { error } = await auth.signupAndLogin({
   *   email: 'user@example.com',
   *   password: '••••••••',
   *   redirectTo: 'https://app.example.com/callback'
   * })
   * if (error) showError(error.message) // e.g. email_exists, weak_password
   * // otherwise the browser is already navigating to complete the login
   * ```
   * @category Sign in
   */
  async signupAndLogin(
    data: SignupParams & { password: string } & SignupLoginOptions
  ): Promise<AuthResult<null>> {
    if (!data?.password) {
      return {
        data: null,
        error: new AuthUnknownError(
          'password is required to sign in after signup',
          null
        )
      }
    }
    const { error } = await this.signup(data)
    if (error) return { data: null, error }

    // Auto-login through the standard redirect flow (no ROPC grant exists for
    // database connections, so this is the same path a manual login takes).
    return this.signInWithUsernamePassword({
      username: (data.email || data.username) as string,
      password: data.password,
      redirectTo: data.redirectTo,
      state: data.state,
      audience: data.audience
    })
  }

  /**
   * Registers a new user and, by default, signs them in.
   *
   * Kept for compatibility: with `signIn` unset or `true` it is
   * {@link FaableAuthClient.signupAndLogin} (the browser navigates on
   * success); with `signIn: false` it is {@link FaableAuthClient.signup} and
   * returns the created user. New code should call one of those two directly.
   *
   * @param data The new user, plus `signIn` and where the login lands.
   * @example
   * ```ts
   * const { error } = await auth.signUp({
   *   email: 'user@example.com',
   *   password: '••••••••',
   *   name: 'Ada Lovelace',
   *   redirectTo: 'https://app.example.com/callback'
   * })
   * if (error) showError(error.message) // e.g. 'email_exists', 'signup_disabled'
   * // otherwise the browser is already navigating to complete the login
   * ```
   * @see {@link https://faable.com/docs/auth/connections | Connections}
   * @category Sign in
   */
  async signUp(
    data: SignupParams & SignupLoginOptions & { signIn: false }
  ): Promise<AuthResult<SignupResult>>
  async signUp(
    data: SignupParams & SignupLoginOptions & { signIn?: true }
  ): Promise<AuthResult<null>>
  async signUp(
    data: SignupParams & SignupLoginOptions & { signIn?: boolean }
  ): Promise<AuthResult<SignupResult> | AuthResult<null>> {
    const { signIn = true, ...rest } = data ?? ({} as typeof data)
    if (!signIn) return this.signup(rest)
    if (!rest.password || (!rest.email && !rest.username)) {
      return {
        data: null,
        error: new AuthUnknownError(
          'email (or username) and password are required',
          null
        )
      }
    }
    return this.signupAndLogin({ ...rest, password: rest.password })
  }

  /**
   * Completes a passwordless login by exchanging an OTP code for a session.
   *
   * Pair this with {@link FaableAuthClient.signInWithPasswordless} called
   * with `type: 'code'`. On success the new session is persisted to storage
   * and a `SIGNED_IN` event is broadcast.
   *
   * @param data The user identifier and the OTP code they received.
   * @example
   * ```ts
   * const { data, error } = await auth.signInWithOtp({
   *   username: 'user@example.com',
   *   otp: '123456'
   * })
   * ```
   * @see {@link https://faable.com/docs/auth/passwordless | Passwordless Authentication}
   * @category Sign in
   */
  async signInWithOtp(data: {
    username: string
    otp: string
    audience?: string
  }): Promise<AuthResponse> {
    const audience = data.audience ?? this.audience
    const rawResponse = await _post<Partial<RawAuthResponse>>(
      `${this.domainUrl}/oauth/token`,
      {
        client_id: this.clientId,
        grant_type: 'http://auth0.com/oauth/grant-type/passwordless/otp',
        username: data.username,
        otp: data.otp,
        ...(audience ? { audience } : {})
      }
    )

    // The tenant may require a second factor. This grant has no browser to
    // redirect, so the server answers 403 with a token to come back with —
    // surfaced as a typed error the caller can branch on rather than a
    // generic failure it would show as "login failed".
    const challenge = _mfaChallenge(rawResponse)
    if (challenge) {
      return { data: { user: null, session: null }, error: challenge }
    }

    const { data: sessionData, error } = _sessionResponse(rawResponse)

    if (error) {
      return { data: { user: null, session: null }, error }
    }

    if (!sessionData || !sessionData.session) {
      return {
        data: { user: null, session: null },
        error: new AuthInvalidTokenResponseError()
      }
    }

    const session = sessionData.session as Session
    const { data: user, error: userError } = await this._getUser(
      session.access_token
    )

    if (userError || !user) {
      return {
        data: { user: null, session: null },
        error:
          userError || new AuthUnknownError('Could not fetch user info', null)
      }
    }

    session.user = user
    await this._saveSession(session)
    await this.clearPendingPasswordless()
    await this._notifyAllSubscribers('SIGNED_IN', session)

    // OTP is the one interactive flow that establishes the session in-page,
    // so the confirmed method is recorded directly (no attempt/promotion).
    this._recordLastUsedMethod({ method: 'otp' })

    return {
      data: { user: session.user, session },
      error: null
    }
  }

  /**
   * Finishes a sign-in that a second-factor policy interrupted.
   *
   * Call it with the `mfa_token` from an {@link AuthMfaRequiredError} and the
   * code the user typed — six digits from their authenticator app, or one of
   * their recovery codes.
   *
   * A wrong code does NOT consume the token: prompt again with the same one
   * instead of restarting the sign-in. The token does expire (a few minutes),
   * after which the flow has to start over.
   *
   * @example
   * ```ts
   * const { error } = await auth.signInWithOtp({ username, otp })
   * if (isAuthMfaRequiredError(error)) {
   *   const code = await askUserForTheirCode()
   *   await auth.signInWithMfa({ mfa_token: error.mfa_token, code })
   * }
   * ```
   * @category Sign in
   */
  async signInWithMfa(data: {
    mfa_token: string
    code: string
    /** Defaults to `otp` — the authenticator app. */
    type?: 'otp' | 'recovery_code'
    audience?: string
  }): Promise<AuthResponse> {
    const audience = data.audience ?? this.audience
    const is_recovery = data.type === 'recovery_code'
    const rawResponse = await _post<Partial<RawAuthResponse>>(
      `${this.domainUrl}/oauth/token`,
      {
        client_id: this.clientId,
        grant_type: is_recovery
          ? 'http://auth0.com/oauth/grant-type/mfa-recovery-code'
          : 'http://auth0.com/oauth/grant-type/mfa-otp',
        mfa_token: data.mfa_token,
        ...(is_recovery ? { recovery_code: data.code } : { otp: data.code }),
        ...(audience ? { audience } : {})
      }
    )

    const { data: sessionData, error } = _sessionResponse(rawResponse)
    if (error) {
      return { data: { user: null, session: null }, error }
    }
    if (!sessionData?.session) {
      return {
        data: { user: null, session: null },
        error: new AuthInvalidTokenResponseError()
      }
    }

    const session = sessionData.session as Session
    const { data: user, error: userError } = await this._getUser(
      session.access_token
    )
    if (userError || !user) {
      return {
        data: { user: null, session: null },
        error:
          userError || new AuthUnknownError('Could not fetch user info', null)
      }
    }

    session.user = user
    await this._saveSession(session)
    await this.clearPendingPasswordless()
    // Both events, in this order: subscribers that only care about "is there
    // a session" keep working unchanged, and those gating on assurance level
    // get the specific signal they need.
    await this._notifyAllSubscribers('MFA_CHALLENGE_VERIFIED', session)
    await this._notifyAllSubscribers('SIGNED_IN', session)
    this._recordLastUsedMethod({ method: 'otp' })

    return { data: { user: session.user, session }, error: null }
  }

  /**
   * Starts a passwordless login flow by emailing the user either an OTP code
   * or a magic link.
   *
   * - `type: 'code'` sends a short code the user pastes into your UI; finish
   *   the flow with {@link FaableAuthClient.signInWithOtp}.
   * - `type: 'link'` sends a clickable link that lands on your `redirectUri`
   *   with the tokens already attached, processed by
   *   {@link FaableAuthClient.initialize} on page load.
   *
   * A `code` that was sent is remembered until it is spent: read it back
   * with {@link FaableAuthClient.getPendingPasswordless} when the screen that
   * asked for it is opened again (another tab, an in-app browser that closed
   * while the user read the email). Any sign-in or sign-out on this client
   * forgets it.
   *
   * @param data The user's email and the delivery mechanism.
   *   - `authParams`: profile for the account a code creates (signup), as the
   *     server's `auth_params`.
   *   - `resumeUrl`: a page of yours the code email links back to, for when
   *     it is opened where this browser's storage is not. Must be on one of
   *     the client's registered origins.
   *   - `appState`: kept with the pending code, never sent.
   * @example
   * ```ts
   * await auth.signInWithPasswordless({
   *   email: 'user@example.com',
   *   type: 'code'
   * })
   * ```
   * @see {@link https://faable.com/docs/auth/passwordless | Passwordless Authentication}
   * @category Sign in
   */
  async signInWithPasswordless(data: {
    email: string
    type: 'code' | 'link'
    audience?: string
    authParams?: Record<string, string>
    resumeUrl?: string
    appState?: unknown
  }): Promise<AuthResult<any>> {
    const audience = data.audience ?? this.audience
    const response = await _post(`${this.domainUrl}/passwordless/start`, {
      client_id: this.clientId,
      email: data.email,
      send: data.type,
      ...(audience ? { audience } : {}),
      ...(data.authParams ? { auth_params: data.authParams } : {}),
      ...(data.resumeUrl ? { resume_url: data.resumeUrl } : {})
    })

    if (!response.error && data.type === 'code') {
      await this.resumePasswordless({
        email: data.email,
        sent_at: Date.now(),
        ...(data.appState !== undefined ? { appState: data.appState } : {})
      })
    }

    return { data: response.data, error: response.error }
  }

  /**
   * The emailed code this browser is waiting for, or `null`. Written by
   * {@link FaableAuthClient.signInWithPasswordless} (`type: 'code'`) and
   * forgotten on any sign-in or sign-out of this client, and 30 minutes after
   * it was written.
   *
   * @example
   * ```ts
   * const pending = await auth.getPendingPasswordless()
   * if (pending) showCodeStep(pending.email)
   * ```
   * @category Sign in
   */
  async getPendingPasswordless(): Promise<PendingPasswordless | null> {
    try {
      return await loadPendingPasswordless(this.storage, this.storageKey)
    } catch (error) {
      this._debug('#getPendingPasswordless()', 'failed', error)
      return null
    }
  }

  /**
   * Records a code the user is waiting for that this browser did not ask for
   * — typically one whose email linked back to your `resumeUrl` from another
   * device. Same lifecycle as the one `signInWithPasswordless` writes.
   *
   * @category Sign in
   */
  async resumePasswordless(pending: PendingPasswordless): Promise<void> {
    try {
      await savePendingPasswordless(this.storage, this.storageKey, pending)
    } catch (error) {
      // Blocked storage: the screen that asked still has it in memory.
      this._debug('#resumePasswordless()', 'failed', error)
    }
  }

  /**
   * Forgets the pending code ("Change email", "Use another account").
   *
   * @category Sign in
   */
  async clearPendingPasswordless(): Promise<void> {
    try {
      await clearPendingPasswordless(this.storage, this.storageKey)
    } catch (error) {
      this._debug('#clearPendingPasswordless()', 'failed', error)
    }
  }

  /**
   * Triggers a "change your password" email for a database-connection user.
   *
   * The current session is unaffected — the user clicks the link in the
   * email and completes the reset on the tenant's hosted pages. The promise
   * resolves once the email has been queued.
   *
   * @param params The user's email address.
   * @example
   * ```ts
   * await auth.changePassword({ email: 'user@example.com' })
   * ```
   * @see {@link https://faable.com/docs/auth/connections | Connections}
   * @category Account
   */
  async changePassword(params: {
    email: string
  }): Promise<AuthResult<unknown>> {
    if (!params?.email) {
      return {
        data: null,
        error: new AuthUnknownError('email is required', null)
      }
    }

    const { data, error } = await _post(
      `${this.domainUrl}/dbconnections/change_password`,
      { email: params.email }
    )
    return {
      data: data ?? null,
      error: error ? new AuthUnknownError(String(error), error) : null
    }
  }

  /**
   * Resolves the signed-in user's id from a session. Prefers the userinfo
   * shape (`user.id`) returned by `/me`, falls back to a `sub` claim on the
   * user object, and finally decodes the access token's `sub` — the id is
   * always present there, so this never reports a live session as missing.
   */
  private _resolveUserId(session: Session): string | undefined {
    const fromUser = (session.user as any)?.id ?? session.user?.sub
    if (fromUser) return fromUser
    try {
      const payload = decodeJWTPayload(session.access_token)
      return payload?.sub
    } catch {
      return undefined
    }
  }

  /**
   * Starts a verified email change for the currently signed-in user.
   *
   * The user must be authenticated — the call is made with the session's
   * access token, and the auth server only lets a user change their own
   * email. It creates a verification ticket and emails the user; the change
   * is applied only after they click the link, which the server handles and
   * then redirects to `redirect_uri`. The current session is unaffected until
   * then.
   *
   * @param params The new email plus optional verification policy.
   *   - `verification_mode`: `'new_only'` verifies just the new address;
   *     `'old_and_new'` also requires confirming from the old one. When
   *     omitted the account's default policy applies.
   *   - `redirect_uri`: where the server sends the user after they verify.
   * @example
   * ```ts
   * const { data, error } = await auth.changeEmail({
   *   new_email: 'new@example.com',
   *   redirect_uri: 'https://app.example.com/account'
   * })
   * // data: { status: 'verification_sent', ticket_id, verification_mode }
   * ```
   * @see {@link https://faable.com/docs/auth/change-email | Change Email}
   * @category Account
   */
  async changeEmail(params: {
    new_email: string
    verification_mode?: 'new_only' | 'old_and_new'
    redirect_uri?: string
  }): Promise<AuthResult<unknown>> {
    if (!params?.new_email) {
      return {
        data: null,
        error: new AuthUnknownError('new_email is required', null)
      }
    }

    const { data: sessionData, error: sessionError } = await this.getSession()
    const session = sessionData?.session
    if (sessionError || !session) {
      return {
        data: null,
        error: sessionError || new AuthSessionMissingError()
      }
    }

    // Resolve the user id defensively. `/me` (userinfo) exposes it as
    // `user.id`, but older/other shapes use the `sub` claim; fall back to the
    // access token's own `sub` so we never mistake a present session for a
    // missing one (that regression shipped `changeEmail` reading only `.sub`).
    const user_id = this._resolveUserId(session)
    if (!user_id) {
      return { data: null, error: new AuthSessionMissingError() }
    }

    const { data, error } = await _post(
      `${this.domainUrl}/user/${user_id}/change-email`,
      {
        new_email: params.new_email,
        ...(params.verification_mode
          ? { verification_mode: params.verification_mode }
          : {}),
        ...(params.redirect_uri ? { redirect_uri: params.redirect_uri } : {})
      },
      { token: session.access_token }
    )
    return {
      data: data ?? null,
      error: error ? new AuthUnknownError(String(error), error) : null
    }
  }

  /**
   * How the signed-in user can sign in: whether they have a password, and
   * which connections (Google, GitHub, the password one…) they are linked to.
   *
   * Use it to choose between "Set a password" and "Change password" on a
   * profile screen. Made with the session's access token; no provider tokens
   * or profile data come back.
   *
   * @example
   * ```ts
   * const { data, error } = await auth.getCredentials()
   * // data: { has_password: false,
   * //         connections: [{ id: 'connection_…', name: 'google', type: 'google_oauth2' }] }
   * ```
   * @see {@link https://faable.com/docs/auth/self-service-account | Self-service account}
   * @category Account
   */
  async getCredentials(): Promise<AuthResult<UserCredentials>> {
    return this._withAccessToken(access_token =>
      this.api.getCredentials({ access_token })
    )
  }

  /**
   * Sets or changes the signed-in user's password, without leaving the app.
   *
   * - **Changing** an existing password needs `current_password`.
   * - **Setting the first one** (a user who signed up with Google, say) needs
   *   a verified email and a recent sign-in: within 5 minutes and, if the user
   *   has a second factor, one that completed it. Otherwise the error code is
   *   `reauth_required`: send the user through {@link FaableAuthClient.authorize}
   *   with `max_age=0` and call again with the new session.
   *
   * The connection's password policy applies (`password_too_weak`). Every
   * other session of the user is signed out; this one stays. The user gets a
   * security email.
   *
   * @param params
   *   - `password`: the new password.
   *   - `current_password`: required when the user already has one.
   * @example
   * ```ts
   * const { data, error } = await auth.setPassword({ password: newPassword })
   * if (error?.code === 'reauth_required') {
   *   // Back here after a fresh sign-in, then retry.
   *   await auth.authorize({ queryParams: { max_age: '0' } })
   * }
   * // data: { revoked_sessions: 2 }
   * ```
   * @see {@link https://faable.com/docs/auth/self-service-account | Self-service account}
   * @category Account
   */
  async setPassword(params: {
    password: string
    current_password?: string
  }): Promise<AuthResult<{ revoked_sessions: number }>> {
    if (!params?.password) {
      return {
        data: null,
        error: new AuthUnknownError('password is required', null)
      }
    }
    return this._withAccessToken(access_token =>
      this.api.setPassword({ access_token, ...params })
    )
  }

  /**
   * Updates the signed-in user's own profile: `name`, `company_name`,
   * `country_iso` and the custom `fields`. Keys left out are not touched.
   *
   * - `country_iso` takes an ISO 3166 code (`ES` or `ESP`) and is stored as
   *   alpha-2; an unknown code fails with `bad_request`.
   * - `fields` are stored in `user_metadata["faable.com/fields"]`. Only the
   *   keys the application declares (`profile_fields` on the client, else on
   *   the tenant) are accepted; any other key, or a value that does not match
   *   its declaration, fails the whole call with `invalid_profile_field`.
   *   Fields are merged by key: keys not named are kept, `value: null`
   *   removes one. The rest of `user_metadata` is untouched.
   *
   * On success the stored session's `user` is updated in place and
   * `USER_UPDATED` fires, so `getSession()` and {@link getProfileField}
   * read the new values right away. The access token's claims follow on
   * the next refresh ({@link FaableAuthClient.refreshSession}).
   *
   * @example
   * ```ts
   * const { data: user, error } = await auth.updateProfile({
   *   name: 'Ada Lovelace',
   *   country_iso: 'GB',
   *   fields: [{ key: 'profile', value: 'teacher' }]
   * })
   * ```
   * @see {@link https://faable.com/docs/auth/self-service-account | Self-service account}
   * @category Account
   */
  async updateProfile(params: ProfileUpdate): Promise<AuthResult<User>> {
    const profile: ProfileUpdate = {}
    if (params?.name !== undefined) profile.name = params.name
    if (params?.company_name !== undefined)
      profile.company_name = params.company_name
    if (params?.country_iso !== undefined)
      profile.country_iso = params.country_iso
    if (params?.fields?.length) profile.fields = params.fields
    if (Object.keys(profile).length === 0) {
      return {
        data: null,
        error: new AuthUnknownError(
          'name, company_name, country_iso or fields is required',
          null
        )
      }
    }
    const result = await this._withAccessToken(access_token =>
      this.api.updateProfile({ access_token, ...profile })
    )
    if (result.data) await this._updateSessionUser(result.data)
    return result
  }

  /**
   * Sets one custom profile field of the signed-in user, or removes it with
   * `null`. Shorthand for `updateProfile({ fields: [{ key, value }] })`: the
   * key must be declared in the application's `profile_fields`, or the call
   * fails with `invalid_profile_field`. Other fields are kept.
   *
   * @example
   * ```ts
   * const { error } = await auth.updateProfileField('invite_card', 'this-app')
   * ```
   * @see {@link https://faable.com/docs/auth/self-service-account | Self-service account}
   * @category Account
   */
  async updateProfileField(
    key: string,
    value: string | null
  ): Promise<AuthResult<User>> {
    return this.updateProfile({ fields: [{ key, value }] })
  }

  /**
   * One custom profile field of the signed-in user (from
   * `user_metadata["faable.com/fields"]` of the session's user), or `null`
   * when it is not set or nobody is signed in. Reads the session, no request.
   *
   * @example
   * ```ts
   * const card = await auth.getProfileField('invite_card')
   * ```
   * @see {@link https://faable.com/docs/auth/self-service-account | Self-service account}
   * @category Account
   */
  async getProfileField(key: string): Promise<string | null> {
    const { data } = await this.getSession()
    const fields = data?.session?.user?.user_metadata?.['faable.com/fields']
    const field = Array.isArray(fields)
      ? fields.find(f => f?.key === key)
      : undefined
    return typeof field?.value === 'string' ? field.value : null
  }

  /**
   * The signed-in user's active sessions — every browser, app and CLI login —
   * newest first, and which one is this. For a "Where you're signed in" list.
   * End the others with `signOut({ scope: 'others' })`.
   *
   * @example
   * ```ts
   * const { data, error } = await auth.listSessions()
   * // data: { results: [{ id: 'session_…', user_agent: '…', ip: '…', … }],
   * //         current_session_id: 'session_…' }
   * ```
   * @see {@link https://faable.com/docs/auth/self-service-account | Self-service account}
   * @category Account
   */
  async listSessions(): Promise<AuthResult<UserSessions>> {
    return this._withAccessToken(access_token =>
      this.api.listSessions({ access_token })
    )
  }

  /**
   * Ends every other session of the signed-in user — other browsers, apps
   * and CLI logins — and keeps this one. Same as `signOut({ scope: 'others' })`,
   * plus how many were ended. Their refresh tokens are refused on next use;
   * access tokens already issued keep working until they expire.
   *
   * @example
   * ```ts
   * const { data, error } = await auth.revokeOtherSessions()
   * // data: { revoked: 2 }
   * ```
   * @see {@link https://faable.com/docs/auth/self-service-account | Self-service account}
   * @category Account
   */
  async revokeOtherSessions(): Promise<AuthResult<{ revoked: number }>> {
    return this._withAccessToken(access_token =>
      this.api.revokeOtherSessions({ access_token })
    )
  }

  /**
   * Unlinks one of the signed-in user's social or enterprise accounts (their
   * Google, GitHub, GitLab…), with the provider tokens it held. Pass the
   * connection id from {@link FaableAuthClient.getCredentials}.
   *
   * Refused with `last_sign_in_method` when it would leave the user with no
   * way to sign in: no password, no account on another connection and no
   * passwordless email sign-in. Offer to set a password
   * ({@link FaableAuthClient.setPassword}) or link another account first.
   * The user's sessions stay.
   *
   * @param connection_id - the connection to unlink (`connection_…`).
   * @example
   * ```ts
   * const { data, error } = await auth.unlinkIdentity('connection_…')
   * if (error?.code === 'last_sign_in_method') {
   *   // "Set a password first"
   * }
   * // data: { unlinked: 1 }
   * ```
   * @see {@link https://faable.com/docs/auth/self-service-account | Self-service account}
   * @category Account
   */
  async unlinkIdentity(
    connection_id: string
  ): Promise<AuthResult<{ unlinked: number }>> {
    if (!connection_id) {
      return {
        data: null,
        error: new AuthUnknownError('connection_id is required', null)
      }
    }
    return this._withAccessToken(access_token =>
      this.api.unlinkIdentity({ access_token, connection_id })
    )
  }

  /**
   * Deletes the signed-in user's account: their identities, credentials,
   * second factors and memberships go with it. Permanent — ask the user to
   * confirm first.
   *
   * On success the local session is removed and `SIGNED_OUT` is emitted, as
   * in `signOut({ scope: 'local' })`: the user it belonged to no longer
   * exists. A suspended user is refused (`user_suspended`).
   *
   * @example
   * ```ts
   * const { error } = await auth.deleteAccount()
   * if (!error) router.push('/goodbye')
   * ```
   * @see {@link https://faable.com/docs/auth/self-service-account | Self-service account}
   * @category Account
   */
  async deleteAccount(): Promise<AuthResult<null>> {
    const result = await this._withAccessToken(access_token =>
      this.api.deleteAccount({ access_token })
    )
    if (!result.error) {
      await this._removeSession()
      await this.storage.removeItem(`${this.storageKey}-code-verifier`)
      await this._notifyAllSubscribers('SIGNED_OUT', null)
    }
    return { data: null, error: result.error }
  }

  /**
   * Runs `fn` with the current session's access token, or reports the
   * session as missing without sending anything.
   */
  private async _withAccessToken<T>(
    fn: (access_token: string) => Promise<AuthResult<T>>
  ): Promise<AuthResult<T>> {
    const { data, error } = await this.getSession()
    const access_token = data?.session?.access_token
    if (error || !access_token) {
      return { data: null, error: error || new AuthSessionMissingError() }
    }
    return fn(access_token)
  }

  /**
   * Builds the tenant's `/authorize` URL without redirecting the browser.
   *
   * Useful when you need to render a login link, open the page in a popup,
   * or hand the URL to a different runtime (e.g. a webview). For the
   * everyday "click → redirect" flow use {@link FaableAuthClient.authorize}
   * or {@link FaableAuthClient.signInWithOauthConnection}.
   *
   * The redirect target is resolved with this precedence:
   * `options.redirectTo` → `config.redirectUri` → `window.location.origin`.
   *
   * @returns The fully-qualified authorize URL.
   * @example
   * ```ts
   * const url = auth.buildAuthorizeUrl({
   *   connection: 'google',
   *   redirectTo: 'https://app.example.com/callback'
   * })
   * window.open(url, 'login', 'popup')
   * ```
   * @see {@link https://faable.com/docs/auth/oauth-flows/authorization-code | Authorization Code with PKCE}
   * @category Authorize URLs
   */
  buildAuthorizeUrl(
    options: {
      connection?: string
      redirectTo?: string
      scope?: string
      response_type?: string
      audience?: string
      /**
       * OIDC `acr_values` — the assurance level this request demands.
       * `urn:faable:loa:2` asks for a login that satisfied a second factor,
       * even when the tenant's policy would not have required one. Combine
       * with `prompt: 'login'` to force a fresh step-up rather than reusing an
       * existing single-factor session.
       */
      acr_values?: string | string[]
      /**
       * The methods the hosted login screen shows for this login — connection
       * ids or names, and `'passkey'`. Each must be one the client already
       * offers; the screen keeps the order configured for the client. Use it
       * to open the same client with fewer options, e.g. only the emailed code:
       * `loginMethods: ['email']`. It narrows the screen; `connection` skips it
       * for a single connection instead, and the two cannot be combined.
       * @see {@link https://faable.com/docs/auth/hosted-login | Hosted Login UI}
       */
      loginMethods?: string | string[]
      /**
       * Extra `/authorize` params merged in as-is — e.g.
       * `{ prompt: 'select_account' }` or `{ prompt: 'login' }` to force
       * account selection / re-authentication even when an SSO session exists.
       */
      queryParams?: { [key: string]: string }
    } = {}
  ): string {
    const params: Record<string, string | undefined> = {
      ...options.queryParams,
      client_id: this.clientId,
      redirect_uri:
        options.redirectTo || this.redirectUri || window?.location.origin,
      response_type: resolveResponseType(options, isBrowser()),
      audience: options.audience ?? this.audience,
      scope: options.scope,
      connection: options.connection,
      // Space-delimited, per OIDC Core §3.1.2.1.
      acr_values: Array.isArray(options.acr_values)
        ? options.acr_values.join(' ')
        : options.acr_values,
      login_methods: Array.isArray(options.loginMethods)
        ? options.loginMethods.join(',')
        : options.loginMethods
    }

    const definedParams = Object.fromEntries(
      Object.entries(params).filter(([, value]) => !!value)
    ) as Record<string, string>

    return `${this.domainUrl}/authorize?${new URLSearchParams(definedParams).toString()}`
  }

  /**
   * Builds the tenant's RP-initiated logout URL
   * (`{domain}/logout?client_id=…`).
   *
   * Navigate the browser to it (top-level, not `fetch`) to end the session and
   * clear the auth server's SSO cookie — the only reliable way to do that from
   * another origin. {@link FaableAuthClient.signOut} does this for you by
   * default; use this helper when you want to drive the navigation yourself.
   *
   * @param options.returnTo Where to send the browser after logout, mapped to
   *   the OIDC `post_logout_redirect_uri`. Must be registered as a logout URL
   *   on the client or the server responds `400`.
   * @param options.idTokenHint The `id_token` of the session being ended
   *   (`id_token_hint`). {@link FaableAuthClient.signOut} passes the stored
   *   one; when driving the navigation yourself, take it from
   *   {@link FaableAuthClient.getSession}.
   * @example
   * ```ts
   * window.location.assign(auth.getLogoutUrl({ returnTo: 'https://app.example.com' }))
   * ```
   * @see {@link https://faable.com/docs/auth/oidc/logout | Logout}
   * @category Authorize URLs
   */
  getLogoutUrl(
    options: { returnTo?: string; idTokenHint?: string } = {}
  ): string {
    const params: Record<string, string> = { client_id: this.clientId }
    if (options.returnTo) {
      params.post_logout_redirect_uri = options.returnTo
    }
    // OIDC RP-Initiated Logout §2 `id_token_hint`: the id_token of the session
    // being ended. The server verifies it against its own keys, and a verified
    // hint is what lets it end the session without asking the person to
    // confirm. `signOut()` passes the stored session's id_token by itself.
    if (options.idTokenHint) {
      params.id_token_hint = options.idTokenHint
    }
    return `${this.domainUrl}/logout?${new URLSearchParams(params).toString()}`
  }

  /**
   * Redirects the current window to the tenant's `/authorize` endpoint.
   *
   * Fire-and-forget: there is no return value because the browser navigates
   * away. The session lands back on your `redirectUri`, where
   * {@link FaableAuthClient.initialize} consumes it on the next page load.
   * A `code` request from a browser carries a PKCE challenge, whose verifier
   * that landing exchanges the code with — required on a tenant that
   * enforces OAuth 2.1.
   *
   * @example
   * ```ts
   * auth.authorize({
   *   response_type: 'code',
   *   redirectTo: 'https://app.example.com/callback'
   * })
   * ```
   * @see {@link https://faable.com/docs/auth/oauth-flows/authorization-code | Authorization Code with PKCE}
   * @category Authorize URLs
   */
  async authorize(options: {
    redirectTo?: string
    scope?: string
    response_type: string
    audience?: string
    /** See {@link FaableAuthClient.buildAuthorizeUrl}. */
    acr_values?: string | string[]
    /** See {@link FaableAuthClient.buildAuthorizeUrl}. */
    loginMethods?: string | string[]
    queryParams?: { [key: string]: string }
  }): Promise<void> {
    if (options.response_type !== 'code' || !isBrowser()) {
      windowHelpers.redirect(this.buildAuthorizeUrl(options))
      return
    }
    // The landing exchanges the code with this verifier and repeats this
    // exact redirect_uri (RFC 6749 §4.1.3); without one it has nothing to
    // exchange with, on any tenant.
    const redirectUri =
      options.redirectTo || this.redirectUri || window?.location.origin
    const [codeChallenge, codeChallengeMethod] =
      await getCodeChallengeAndMethod(
        this.storage,
        this.storageKey,
        false,
        undefined,
        { sentState: options.queryParams?.state !== undefined, redirectUri }
      )
    windowHelpers.redirect(
      this.buildAuthorizeUrl({
        ...options,
        redirectTo: redirectUri,
        queryParams: {
          ...options.queryParams,
          code_challenge: codeChallenge,
          code_challenge_method: codeChallengeMethod
        }
      })
    )
  }

  /**
   * Sends the user through a fresh login that must satisfy a second factor,
   * regardless of the tenant's policy or of an existing single-factor session.
   *
   * The pattern for a sensitive action — changing payment details, deleting an
   * organisation — where the application wants a stronger proof than the one
   * the session already carries. Read {@link FaableAuthClient.getAal} first:
   * a session that is already `2` needs no step-up.
   *
   * @example
   * ```ts
   * if ((await auth.getAal()) < 2) {
   *   auth.stepUp({ redirectTo: window.location.href })
   *   return
   * }
   * ```
   * @category Authorize URLs
   */
  stepUp(
    options: { redirectTo?: string; scope?: string; audience?: string } = {}
  ) {
    return this.authorize({
      ...options,
      response_type: isBrowser() ? 'code' : 'token',
      acr_values: 'urn:faable:loa:2',
      // Without this the server may reuse the current single-factor session
      // and hand back exactly the assurance level we are trying to raise.
      queryParams: { prompt: 'login' }
    })
  }

  private async _handleConnectionSignIn(options: {
    connection?: string
    connection_id?: string
    redirectTo?: string
    returnTo?: string
    scopes?: string
    queryParams?: { [key: string]: string }
    skipBrowserRedirect?: boolean
    audience?: string
    appState?: unknown
    // Link mode: `/authorize?link=true` attaches the resulting identity to
    // the ACTIVE session user instead of signing in/up. See
    // linkOauthConnection().
    link?: boolean
    // Silent mode (`prompt=none`, getTokenSilently): a re-authentication the
    // user never sees, so it must not count as their "last used" login.
    silent?: boolean
  }) {
    const url: string = await this._getUrlForConnection(
      `${this.domainUrl}/authorize`,
      {
        response_type: isBrowser() ? 'code' : 'token',
        connection: options.connection,
        connection_id: options.connection_id,
        redirectTo: options.redirectTo,
        returnTo: options.returnTo,
        scopes: options.scopes,
        queryParams: options.link
          ? { ...(options.queryParams ?? {}), link: 'true' }
          : options.queryParams,
        audience: options.audience,
        appState: options.appState,
        link: options.link
      }
    )

    this._debug('#_handleConnectionSignIn()', 'options', options, 'url', url)

    // Must happen before the redirect below: the promise never resolves on
    // the navigation path, so there is no "after". Skipped in link mode: a
    // connect round-trip is not how the user signs in, so it must not flip
    // the "last used login method" hint.
    if (!options.link && !options.silent) {
      await this._saveLoginAttempt({
        method: 'oauth',
        connection: options.connection,
        connection_id: options.connection_id
      })
    }

    // try to open on the browser
    if (isBrowser() && !options.skipBrowserRedirect) {
      window?.location.assign(url)
      // `location.assign()` does not block: the navigation is scheduled but the
      // current tick keeps running. If we resolved here, any loading state a
      // consumer tied to the returned promise would flip back on before the
      // page unloads (a visible flash on the button/spinner). Never resolve on
      // the redirect success path — let the pending navigation own the UI until
      // it unloads. Pass `skipBrowserRedirect: true` to get `{ data: { url } }`
      // back and drive the navigation yourself.
      await new Promise<never>(() => {})
    }

    return { data: { url }, error: null }
  }

  /**
   * Best-effort write of a pending login attempt. A storage failure must
   * never break the sign-in itself, so errors are only logged.
   */
  private async _saveLoginAttempt(attempt: {
    method: LastUsedLoginMethodKind
    connection?: string
    connection_id?: string
  }): Promise<void> {
    try {
      await saveLoginAttempt(this.storage, this.storageKey, attempt)
    } catch (error) {
      this._debug('#_saveLoginAttempt()', 'failed', error)
    }
  }

  /**
   * Promotes the pending login attempt (if any) to the confirmed last-used
   * record. Called from the callback path once a session was established;
   * a login started elsewhere (other tab, other device) leaves no attempt
   * and records nothing.
   */
  private async _promoteLoginAttempt(): Promise<void> {
    try {
      const attempt = await consumeLoginAttempt(this.storage, this.storageKey)
      if (!attempt) return
      this._recordLastUsedMethod(attempt)
    } catch (error) {
      this._debug('#_promoteLoginAttempt()', 'failed', error)
    }
  }

  /** Best-effort write of the confirmed last-used method cookie. */
  private _recordLastUsedMethod(record: {
    method: LastUsedLoginMethodKind
    connection?: string
    connection_id?: string
  }): void {
    try {
      saveLastUsedMethod(
        this.storageKey,
        { ...record, at: Date.now() },
        this.lastUsedCookie
      )
    } catch (error) {
      this._debug('#_recordLastUsedMethod()', 'failed', error)
    }
  }

  /**
   * Returns the login method the user last *completed* on this browser, or
   * `null` when none was recorded — for login UIs that want to show a
   * PostHog-style "Last used" hint next to the matching sign-in button.
   *
   * A method is recorded only when the login actually establishes a session
   * (the OAuth/password redirect round-trip finishing, or an OTP exchange
   * succeeding); a clicked button whose flow was abandoned or failed leaves
   * no trace. The record survives {@link FaableAuthClient.signOut} on
   * purpose and lives in a dedicated cookie, so it can be shared across
   * subdomains via the `lastUsedCookie.domain` client config.
   *
   * @returns The last confirmed method (`oauth` logins carry the
   *   `connection` / `connection_id` they were started with) or `null`.
   * @example
   * ```ts
   * const last = await auth.getLastUsedLoginMethod()
   * if (last?.method === 'oauth') highlight(last.connection_id)
   * ```
   * @see {@link https://faable.com/docs/auth/connections | Connections}
   * @category Sign in
   */
  async getLastUsedLoginMethod(): Promise<LastUsedLoginMethod | null> {
    return loadLastUsedMethod(this.storageKey)
  }

  /**
   * Adopts an externally-provided session into the client.
   *
   * Decodes the access token to find its expiry; refreshes immediately when
   * already expired, otherwise fetches the user info to round-trip the
   * session. Persists the result and broadcasts `SIGNED_IN`. An invalid
   * refresh or access token surfaces as `error` on the returned object.
   *
   * @param currentSession Minimal session shape — an access token and a
   *   refresh token. Other fields are recomputed.
   * @example
   * ```ts
   * // After receiving tokens from a custom server-side handoff
   * await auth.setSession({ access_token, refresh_token })
   * ```
   * @see {@link https://faable.com/docs/auth/oidc/userinfo | UserInfo}
   * @category Sessions
   */
  async setSession(currentSession: {
    access_token: string
    refresh_token: string
  }): Promise<AuthResponse> {
    await this.initializePromise

    return await this.lock._acquireLock(-1, async () => {
      return await this._setSession(currentSession)
    })
  }

  /**
   * Returns the session, refreshing it if necessary.
   *
   * The session returned can be `null` if no user is signed in or the last
   * one has logged out.
   *
   * **IMPORTANT:** This method loads values directly from the storage
   * attached to the client. If that storage is based on request cookies (for
   * example, on the server) the values in it may not be authentic and
   * therefore it's strongly advised against using this method and its
   * results in such circumstances — a warning will be emitted when the
   * storage exposes `isServer: true`. Re-fetch the user with a verified
   * call (or verify the access token yourself) before trusting it.
   *
   * @example
   * ```ts
   * const { data, error } = await auth.getSession()
   * if (data.session) console.log(data.session.user)
   * ```
   * @see {@link https://faable.com/docs/auth/oidc/userinfo | UserInfo}
   * @category Sessions
   */
  async getSession() {
    await this.initializePromise

    const result = await this.lock._acquireLock(-1, async () => {
      return this._useSession(async result => {
        return result
      })
    })

    return result
  }

  /**
   * The claims of the current session's access token, decoded locally — the
   * standard JWT set plus any custom claim the tenant put there (a
   * connection's `claims_mapping`, an Action's
   * `api.accessToken.setCustomClaim`). Refreshes the session first when it
   * has expired, exactly like {@link getSession}, so the claims are always
   * those of a live token; custom claims survive refreshes by design.
   *
   * `claims` is `null` (with no error) when there is no session. The token
   * is decoded, not signature-verified: use the result for UI and routing
   * decisions, never as authorization — that is the resource server's job.
   * Narrow your own claims with the generic parameter.
   *
   * @example
   * ```ts
   * const { data } = await auth.getClaims<{ 'ciapol.com/station_id': string }>()
   * const station = data.claims?.['ciapol.com/station_id']
   * ```
   * @category Sessions
   */
  async getClaims<
    T extends Record<string, unknown> = Record<string, unknown>
  >(): Promise<
    | { data: { claims: JwtClaims & T; session: Session }; error: null }
    | { data: { claims: null; session: null }; error: AuthError | null }
  > {
    const { data, error } = await this.getSession()
    if (error || !data.session) {
      return { data: { claims: null, session: null }, error: error ?? null }
    }
    try {
      const claims = decodeJWTPayload(data.session.access_token) as JwtClaims &
        T
      return { data: { claims, session: data.session }, error: null }
    } catch (e) {
      return {
        data: { claims: null, session: null },
        error: new AuthUnknownError(
          `Could not decode the access token: ${(e as Error).message}`,
          e
        )
      }
    }
  }

  /**
   * One claim of the current session's access token, or `null` when there is
   * no session or the claim is absent. Sugar over {@link getClaims}.
   *
   * @example
   * ```ts
   * const station = await auth.getClaim<string>('ciapol.com/station_id')
   * ```
   * @category Sessions
   */
  async getClaim<T = unknown>(name: string): Promise<T | null> {
    const { data } = await this.getClaims()
    const value = data.claims?.[name]
    return value === undefined ? null : (value as T)
  }

  /**
   * Authenticator Assurance Level of the current session: `2` when it
   * satisfied a second factor (or a passkey that verified the user did both
   * at once), `1` for a single factor, `0` when there is no session.
   *
   * Read locally from the access token's `acr` claim — no network call — so it
   * is cheap enough to gate a render on.
   *
   * @example
   * ```ts
   * if ((await auth.getAal()) < 2) auth.stepUp()
   * ```
   * @category Session
   */
  async getAal(): Promise<0 | 1 | 2> {
    const acr = await this.getClaim<string>('acr')
    if (acr === 'urn:faable:loa:2') return 2
    if (acr === 'urn:faable:loa:1') return 1
    // No `acr` at all means the token predates this, or the tenant never
    // asked for a level. A session exists, so it is at least one factor.
    const { data } = await this.getSession()
    return data?.session ? 1 : 0
  }

  /**
   * Did the current session authenticate with this method?
   *
   * Values follow RFC 8176 as the server emits them: `pwd`, `otp`, `hwk` for
   * a hardware key, `federated` for a social login, and `mfa` once a second
   * factor was satisfied.
   *
   * @example
   * ```ts
   * const usedPasskey = await auth.hasAmr('hwk')
   * ```
   * @category Session
   */
  async hasAmr(method: string): Promise<boolean> {
    const amr = await this.getClaim<string[]>('amr')
    return Array.isArray(amr) && amr.includes(method)
  }

  /**
   * Does the current session's access token carry these permissions (all of
   * them)? Read from the `permissions` claim, which the API emits with the
   * `access_token_authz` dialect; with RBAC on the API, a user token carries
   * the permissions you requested as scopes that the user's roles grant.
   *
   * Decoded locally, not verified: use it to show or hide UI. Your API must
   * check the token itself.
   *
   * @example
   * ```ts
   * if (await auth.hasPermission('invites:write')) showInviteButton()
   * ```
   * @category Sessions
   */
  async hasPermission(permission: string | string[]): Promise<boolean> {
    const { data } = await this.getClaims()
    return hasPermissions(data.claims, permission)
  }

  /**
   * Does the current session's access token carry these roles (all of them)?
   * Read from the `roles` claim, present when the API enables
   * `include_roles_in_access_token`. Same caveat as {@link hasPermission}.
   *
   * @example
   * ```ts
   * const isAdmin = await auth.hasRole('admin')
   * ```
   * @category Sessions
   */
  async hasRole(role: string | string[]): Promise<boolean> {
    const { data } = await this.getClaims()
    return hasRoles(data.claims, role)
  }

  /**
   * Error the auth server returned via redirect on the current page load
   * (`?error=access_denied&error_description=...`, RFC 6749 §4.1.2.1), or
   * `null` when the page wasn't reached through a failed auth round-trip.
   *
   * Consume-once: the first read clears it (and the params were already
   * stripped from the URL at initialization), so a later router navigation
   * can't re-show a stale failure. Exists because the promise returned by
   * {@link signInWithOauthConnection} dies with the top-level navigation —
   * this is the only place the app can learn WHY the round-trip failed
   * (e.g. an action deny like "Signups from this network are currently
   * restricted").
   *
   * @example
   * ```ts
   * const redirectError = await auth.getRedirectError()
   * if (redirectError) showAlert(redirectError.error_description)
   * ```
   * @category Sessions
   */
  async getRedirectError(): Promise<RedirectError | null> {
    await this.initializePromise
    const entry = pageRedirectErrors.get(this.storageKey)
    if (!entry || entry.claimed) return null
    // Claimed before the await: a second caller in the same tick (React
    // StrictMode runs effects twice) gets `null`, never a second copy.
    entry.claimed = true
    return await entry.error
  }

  /**
   * The error of an `?error=` redirect, with what the PKCE record of the
   * round trip that failed remembers: connection, link mode, `appState`,
   * `returnTo`. The record is spent here — the code it was waiting for is
   * never coming. Best-effort: without a record (another browser started it,
   * it expired, storage is blocked) the bare error is still returned.
   */
  private async _redirectErrorWithAttempt(
    base: RedirectError
  ): Promise<RedirectError> {
    try {
      const key = `${this.storageKey}-code-verifier`
      const attempt = await loadCodeVerifier(this.storage, key)
      if (!attempt) return base
      await this.storage.removeItem(key)
      return {
        ...base,
        ...(attempt.connection ? { connection: attempt.connection } : {}),
        ...(attempt.connectionId
          ? { connection_id: attempt.connectionId }
          : {}),
        ...(attempt.link ? { link: true } : {}),
        ...(attempt.appState !== undefined
          ? { appState: attempt.appState }
          : {}),
        ...(attempt.returnTo ? { returnTo: attempt.returnTo } : {})
      }
    } catch (error) {
      this._debug('#_redirectErrorWithAttempt()', 'failed', error)
      return base
    }
  }

  /**
   * Why the SDK last terminated the session on its own (a refresh rejected
   * by the server — e.g. `code: 'user_suspended'`, a revoked grant), or
   * `null` when the last sign-out was voluntary or there was none.
   *
   * Consume-once, like {@link getRedirectError}: the first read clears it so
   * a later visit to the login page can't re-show a stale banner. Persisted
   * in storage (not memory) because the terminating tab usually navigates
   * away — the login page that renders the reason is a fresh document.
   *
   * @example
   * ```ts
   * const reason = await auth.getSignOutReason()
   * if (reason?.code === 'user_suspended') showSuspendedPanel()
   * ```
   * @category Sessions
   */
  async getSignOutReason(): Promise<SignOutReason | null> {
    try {
      const key = this.storageKey + SIGNOUT_REASON_SUFFIX
      const stored = await getItemAsync(this.storage, key)
      if (!stored || typeof stored !== 'object') return null
      await this.storage.removeItem(key)
      const reason = stored as Partial<SignOutReason>
      if (typeof reason.message !== 'string') return null
      return {
        message: reason.message,
        ...(typeof reason.code === 'string' ? { code: reason.code } : {}),
        at: typeof reason.at === 'number' ? reason.at : 0
      }
    } catch (e) {
      // Storage can be unavailable (SSR, privacy modes) — a missing reason
      // must never break the login page.
      this._debug('#getSignOutReason()', 'error', e)
      return null
    }
  }

  /**
   * Persist the reason for an SDK-initiated session termination. Voluntary
   * {@link signOut} never writes this — only terminal refresh failures do.
   */
  private async _stashSignOutReason(error: AuthError) {
    try {
      const reason: SignOutReason = {
        message: error.message,
        ...(error.code ? { code: error.code } : {}),
        at: Date.now()
      }
      await setItemAsync(
        this.storage,
        this.storageKey + SIGNOUT_REASON_SUFFIX,
        reason
      )
    } catch (e) {
      // Best-effort: losing the reason only costs the login page some
      // context; it must never interfere with the sign-out itself.
      this._debug('#_stashSignOutReason()', 'error', e)
    }
  }

  /**
   * Use instead of {@link #getSession} inside the library. It is
   * semantically usually what you want, as getting a session involves some
   * processing afterwards that requires only one client operating on the
   * session at once across multiple tabs or processes.
   */
  private async _useSession<R>(
    fn: (
      result:
        | {
            data: {
              session: Session
            }
            error: null
          }
        | {
            data: {
              session: null
            }
            error: AuthError
          }
        | {
            data: {
              session: null
            }
            error: null
          }
    ) => Promise<R>
  ): Promise<R> {
    this._debug('#_useSession', 'begin')

    try {
      // the use of __loadSession here is the only correct use of the function!
      const result = await this.__loadSession()

      return await fn(result)
    } finally {
      this._debug('#_useSession', 'end')
    }
  }

  /**
   * NEVER USE DIRECTLY!
   *
   * Always use {@link #_useSession}.
   */
  private async __loadSession(): Promise<
    | {
        data: {
          session: Session
        }
        error: null
      }
    | {
        data: {
          session: null
        }
        error: AuthError
      }
    | {
        data: {
          session: null
        }
        error: null
      }
  > {
    this._debug('#__loadSession()', 'begin')

    if (!this.lock.lockAcquired) {
      this._debug(
        '#__loadSession()',
        'used outside of an acquired lock!',
        new Error().stack
      )
    }

    try {
      let currentSession: Session | null = null

      const maybeSession = await getItemAsync(this.storage, this.storageKey)

      this._debug('#getSession()', 'session from storage', maybeSession)

      if (maybeSession !== null) {
        if (isValidSession(maybeSession)) {
          currentSession = maybeSession
        } else {
          this._debug('#getSession()', 'session from storage is not valid')
          await this._removeSession()
        }
      }

      if (!currentSession) {
        return { data: { session: null }, error: null }
      }

      const hasExpired = currentSession.expires_at
        ? currentSession.expires_at <= Date.now() / 1000
        : false

      this._debug(
        '#__loadSession()',
        `session has${hasExpired ? '' : ' not'} expired`,
        'expires_at',
        currentSession.expires_at
      )

      if (!hasExpired) {
        if (this.storage.isServer) {
          const proxySession: Session = new Proxy(currentSession, {
            get(target: any, prop: string, receiver: any) {
              if (prop === 'user') {
                // only show warning when the user object is being accessed from the server
                console.warn(
                  'Reading `session.user` from a server-side cookie store can be insecure: the value is whatever the cookie contains and has not been verified against Faable Auth. Re-fetch the user with `auth.getUser()` (or verify the access token yourself) before trusting it on the server.'
                )
              }
              return Reflect.get(target, prop, receiver)
            }
          })
          currentSession = proxySession
        }

        return { data: { session: currentSession }, error: null }
      }

      const { session, error } = await this._callRefreshToken(
        currentSession.refresh_token
      )
      if (error) {
        return { data: { session: null }, error }
      }

      return { data: { session }, error: null }
    } finally {
      this._debug('#__loadSession()', 'end')
    }
  }

  private async _removeSession() {
    this._debug('#_removeSession()')
    this._session = null
    await this.storage.removeItem(this.storageKey)
  }

  protected async _setSession(currentSession: {
    access_token: string
    refresh_token: string
  }): Promise<AuthResponse> {
    try {
      if (!currentSession.access_token || !currentSession.refresh_token) {
        throw new AuthSessionMissingError()
      }

      const timeNow = Date.now() / 1000
      let expiresAt = timeNow
      let hasExpired = true
      let session: Session | null = null
      const payload = decodeJWTPayload(currentSession.access_token)
      if (payload.exp) {
        expiresAt = payload.exp
        hasExpired = expiresAt <= timeNow
      }

      if (hasExpired) {
        const { session: refreshedSession, error } =
          await this._callRefreshToken(currentSession.refresh_token)
        if (error) {
          return { data: { user: null, session: null }, error: error }
        }

        if (!refreshedSession) {
          return { data: { user: null, session: null }, error: null }
        }
        session = refreshedSession
      } else {
        const { data: user, error } = await this._getUser(
          currentSession.access_token
        )
        if (error || !user) {
          throw error
        }
        session = {
          access_token: currentSession.access_token,
          refresh_token: currentSession.refresh_token,
          // Typed as the token pair only; the stored object is a full session.
          id_token: (currentSession as Partial<Session>).id_token,
          user,
          token_type: 'bearer',
          expires_in: expiresAt - timeNow,
          expires_at: expiresAt
        }
        await this._saveSession(session)
        await this._notifyAllSubscribers('SIGNED_IN', session)
      }

      return { data: { user: session.user, session }, error: null }
    } catch (error) {
      if (isAuthError(error)) {
        return { data: { session: null, user: null }, error }
      }

      throw error
    }
  }

  /**
   * set currentSession and currentUser
   * process to _startAutoRefreshToken if possible
   */
  private async _saveSession(session: Session) {
    this._debug('#_saveSession()', session)
    this._session = session

    await setItemAsync(this.storage, this.storageKey, session)
  }

  /**
   * Merges `user` into the stored session and tells subscribers
   * (`USER_UPDATED`): what the account routes return is the user as it is now.
   */
  private async _updateSessionUser(user: User) {
    await this.lock._acquireLock(-1, async () => {
      await this._useSession(async ({ data }) => {
        const session = data.session
        if (!session) return
        const next = { ...session, user: { ...session.user, ...user } }
        await this._saveSession(next)
        await this._notifyAllSubscribers('USER_UPDATED', next)
      })
    })
  }

  private async _getUser(access_token: string) {
    if (!access_token) throw new Error('Cannot fetch user without token')
    this._debug('#_getUser() begin')
    const res = await _get<User>(`${this.domainUrl}/me`, {
      token: access_token
    })
    this._debug('#_getUser() end')
    return {
      data: res.data,
      error: res.error,
      status: res.status,
      code: res.code
    }
  }

  private async _callRefreshToken(refreshToken: string) {
    if (!refreshToken) {
      throw new AuthSessionMissingError()
    }

    // refreshing is already in progress
    if (this.refreshingDeferred) {
      return this.refreshingDeferred.promise
    }

    const debugName = `#_callRefreshToken(${refreshToken.substring(0, 5)}...)`

    this._debug(debugName, 'begin')

    try {
      this.refreshingDeferred = new Deferred<CallRefreshTokenResult>()

      const { data, error } = await withTimeout(
        this._refreshAccessToken(refreshToken),
        REFRESH_TIMEOUT_MS,
        // A REAL AuthError, and specifically a retryable one. As a plain
        // `Error` this fell past the `isAuthError` branch below to the only
        // exit of this method that THROWS instead of returning
        // `{ session, error }` — no stashed reason, no session removal, no
        // SIGNED_OUT. Callers that do not catch (`__loadSession`, and so
        // `getSession()`) rejected instead of resolving, which strands a
        // consumer that awaits the session on boot: the dashboard's loading
        // screen hung forever on 2026-09-04.
        //
        // Retryable is the honest classification: a deadline elapsing is not a
        // verdict from the server, so the session must survive it — same
        // treatment a dropped connection already gets. Status 0 is this file's
        // convention for "no server verdict".
        () => new AuthRetryableFetchError('Token refresh timed out', 0)
      )
      if (error) throw error
      if (!data.session) throw new AuthSessionMissingError()

      await this._saveSession(data.session)
      await this._notifyAllSubscribers('TOKEN_REFRESHED', data.session)

      const result = { session: data.session, error: null }

      // Optional, like the two below: `finally` nulls this field, and a
      // concurrent path can get there first.
      this.refreshingDeferred?.resolve(result)

      return result
    } catch (error) {
      this._debug(debugName, 'error', error)

      if (isAuthError(error)) {
        const result = { session: null, error }

        if (!isAuthRetryableFetchError(error)) {
          // The SDK is about to end the session on its own — leave a
          // consume-once record of WHY, so the login screen the user lands
          // on can say "account suspended" instead of going mute. Written
          // BEFORE the removal: the SIGNED_OUT subscribers may navigate.
          await this._stashSignOutReason(error)
          await this._removeSession()
          await this._notifyAllSubscribers('SIGNED_OUT', null)
        }

        this.refreshingDeferred?.resolve(result)

        return result
      }

      this.refreshingDeferred?.reject(error)
      throw error
    } finally {
      this.refreshingDeferred = null
      this._debug(debugName, 'end')
    }
  }

  /**
   * Generates a new JWT.
   * @param refreshToken A valid refresh token that was returned on login.
   */
  private async _refreshAccessToken(
    refreshToken: string
  ): Promise<AuthResponse> {
    const debugName = `#_refreshAccessToken(${refreshToken.substring(0, 5)}...)`
    this._debug(debugName, 'begin')

    try {
      const startedAt = Date.now()

      // will attempt to refresh the token with exponential backoff

      return await retryable(
        async attempt => {
          if (attempt > 0) {
            await sleep(200 * Math.pow(2, attempt - 1)) // 200, 400, 800, ...
          }

          this._debug(debugName, 'refreshing attempt', attempt)

          // return await _post(`${this.url}/token?grant_type=refresh_token`, {
          //   body: { refresh_token: refreshToken },
          //   headers: this.headers,
          //   xform: _sessionResponse,
          // });
          const rawResponse = await _post<Partial<RawAuthResponse>>(
            `${this.domainUrl}/oauth/token`,
            {
              client_id: this.clientId,
              grant_type: 'refresh_token',
              refresh_token: refreshToken,
              ...(this.audience ? { audience: this.audience } : {})
            }
          )
          if (rawResponse.error) {
            // No HTTP status → the request never completed (network down,
            // CORS, aborted); a 5xx → the server broke. Neither is a verdict
            // on the grant, so both are retryable — historically EVERY
            // failure here became a non-retryable AuthUnknownError and one
            // wifi blip during a refresh silently signed the user out. With
            // short-lived access tokens (refresh every ~30 min) that would
            // be constant, so only a definitive 4xx may kill the session.
            if (rawResponse.status === undefined || rawResponse.status >= 500) {
              throw new AuthRetryableFetchError(
                rawResponse.error instanceof Error
                  ? rawResponse.error.message
                  : String(rawResponse.error),
                rawResponse.status ?? 0
              )
            }
            // Definitive server rejection: surface the RFC 6749 body as a
            // typed AuthApiError carrying the stable machine code
            // (`user_suspended`, …) so the sign-out reason can record WHY.
            const { error: api_error } = _sessionResponse(rawResponse)
            throw (
              api_error ??
              new AuthUnknownError(
                `Refresh token request failed: ${rawResponse.error}`,
                rawResponse.error
              )
            )
          }
          const session_res = _sessionResponse(rawResponse)

          if (!session_res.data.session?.access_token) {
            throw new AuthInvalidTokenResponseError()
          }
          const {
            data: user,
            error,
            status: user_status,
            code: user_code
          } = await this._getUser(session_res.data.session?.access_token)

          if (error) {
            // Same classification as the grant above: no status / 5xx is
            // transient (the freshly minted token is fine — retry), a 4xx is
            // a real denial (e.g. the user was suspended between the grant
            // and this read) and carries its machine code.
            if (user_status === undefined || user_status >= 500) {
              throw new AuthRetryableFetchError(
                'Could not fetch user info',
                user_status ?? 0
              )
            }
            throw new AuthApiError(
              error instanceof Error ? error.message : String(error),
              user_status,
              user_code
            )
          }
          if (!user) {
            throw new AuthUnknownError('Refresh response missing user', null)
          }

          const x = {
            data: {
              session: {
                ...session_res.data.session,
                user
              },
              user
            },
            error: null
          }
          // this._debug(x);
          return x
        },
        (attempt, error) => {
          const nextBackOffInterval = 200 * Math.pow(2, attempt)
          return (
            error &&
            isAuthRetryableFetchError(error) &&
            // retryable only if the request can be sent before the backoff overflows the tick duration
            Date.now() + nextBackOffInterval - startedAt <
              AUTO_REFRESH_TICK_DURATION
          )
        }
      )
    } catch (error) {
      this._debug(debugName, 'error', error)

      if (isAuthError(error)) {
        return { data: { session: null, user: null }, error }
      }
      throw error
    } finally {
      this._debug(debugName, 'end')
    }
  }

  private async _notifyAllSubscribers(
    event: AuthChangeEvent,
    session: Session | null,
    broadcast = true
  ) {
    const debugName = `#_notifyAllSubscribers(${event})`
    this._debug(debugName, 'begin', session, `broadcast = ${broadcast}`)
    try {
      await this.broadcastSync.notify(event, session, broadcast)
    } finally {
      this._debug(debugName, 'end')
    }
  }

  /**
   * Signs the user out and clears the session from storage.
   *
   * In a browser context this removes the persisted session and broadcasts a
   * `SIGNED_OUT` event to every tab listening on the same `storageKey`. The
   * access token JWT itself remains valid until its `exp` — keep that
   * expiry short.
   *
   * **By default (`'app'` scope) it signs the user out of this app only**: its
   * refresh token is revoked at the auth server (RFC 7009) and its storage
   * cleared, with no navigation. The tenant's SSO session stays, so the
   * user's other apps on the same tenant keep theirs, and the next sign-in
   * here comes back without asking.
   *
   * To end the SSO session too, pass `{ scope: 'global' }`: in a browser the
   * page navigates to the auth server's `/logout` (a cross-origin `fetch`
   * cannot clear that cookie) and returns to `returnTo` if given; the
   * returned promise does not resolve on that path. `{ scope: 'global',
   * redirect: false }` keeps the legacy fetch-only behaviour, or use
   * {@link FaableAuthClient.getLogoutUrl} to drive the navigation yourself.
   *
   * Scopes:
   * - `'app'` (default) — this app only, as above. The revoke is best-effort:
   *   the local sign-out happens whatever it answers.
   * - `'global'` — ends **this browser's** session at the auth server: every
   *   refresh token issued in it is refused from then on, in every app of the
   *   tenant, and in a browser the page navigates to `/logout` to clear the
   *   SSO cookie. Sessions on other devices are untouched — that is what
   *   `'others'` is for.
   * - `'local'` — only clear this client's storage. Nothing is sent to the
   *   server: the SSO session and its refresh tokens stay valid (no redirect).
   * - `'others'` — `POST /me/sessions/revoke-others`: ends every session of
   *   the user **except this one**. The local session stays, no `SIGNED_OUT`
   *   event is fired and there is no redirect. Needs a live access token.
   *
   * @example
   * ```ts
   * await auth.signOut() // this app only; the user's other apps stay signed in
   * await auth.signOut({ scope: 'global' }) // + the SSO session, via /logout
   * await auth.signOut({ scope: 'global', returnTo: 'https://app.example.com/bye' })
   * await auth.signOut({ scope: 'local' }) // only this device's storage
   * await auth.signOut({ scope: 'others' }) // every other device, stay signed in
   * ```
   * @see {@link https://faable.com/docs/auth/oidc/logout | Logout}
   * @category Sign out
   */
  async signOut(
    options: SignOut = { scope: 'app' }
  ): Promise<{ error: AuthError | null }> {
    await this.initializePromise

    return await this.lock._acquireLock(-1, async () => {
      return await this._signOut(options)
    })
  }

  protected async _signOut(
    { scope = 'app', redirect, returnTo }: SignOut = { scope: 'app' }
  ): Promise<{ error: AuthError | null }> {
    return await this._useSession(async result => {
      const { data, error: sessionError } = result
      if (sessionError) {
        return { error: sessionError }
      }

      // "Everywhere but here": the server revokes the user's other sessions
      // and this one stays exactly as it is — no local teardown, no event.
      if (scope === 'others') {
        const access_token = data.session?.access_token
        if (!access_token) {
          return { error: new AuthSessionMissingError() }
        }
        const { error } = await this.api.revokeOtherSessions({ access_token })
        return { error }
      }

      // App scope (the default): this app's refresh token is revoked at the
      // server (RFC 7009) and its storage cleared. The tenant's SSO session
      // stays, so every other app of the tenant keeps its own — signing out of
      // one app must not sign the person out of all of them. The revoke is
      // best-effort: whatever it answers, the local teardown runs.
      if (scope === 'app') {
        const refresh_token = data.session?.refresh_token
        let revokeError: AuthError | null = null
        if (refresh_token) {
          const { error } = await this.api.revokeToken({
            token: refresh_token,
            client_id: this.clientId
          })
          if (error && isAuthApiError(error)) revokeError = error
        }
        await this._removeSession()
        await this.storage.removeItem(`${this.storageKey}-code-verifier`)
        await this.clearPendingPasswordless()
        await this._notifyAllSubscribers('SIGNED_OUT', null)
        return { error: revokeError }
      }

      // Local scope is storage only, by contract: the SSO session at the auth
      // server — and every refresh token it issued — stays valid.
      if (scope === 'local') {
        await this._removeSession()
        await this.storage.removeItem(`${this.storageKey}-code-verifier`)
        await this.clearPendingPasswordless()
        await this._notifyAllSubscribers('SIGNED_OUT', null)
        return { error: null }
      }

      // RP-initiated logout: a top-level navigation to /logout is the only way
      // to clear the auth server's SSO cookie from another origin (a cross-site
      // fetch can neither send nor clear it). Default for the global scope in a
      // browser; opt out with { redirect: false }.
      const shouldRedirect =
        scope === 'global' && redirect !== false && isBrowser()
      if (shouldRedirect) {
        // Tear down local state first — the page unloads on the navigation.
        await this._removeSession()
        await this.storage.removeItem(`${this.storageKey}-code-verifier`)
        await this.clearPendingPasswordless()
        await this._notifyAllSubscribers('SIGNED_OUT', null)
        windowHelpers.redirect(
          this.getLogoutUrl({ returnTo, idTokenHint: data.session?.id_token })
        )
        // The browser is navigating away — never resolve, so a loading state
        // tied to the await doesn't flip back before the page unloads.
        await new Promise<never>(() => {})
      }

      // Non-redirect path (opted out, or non-browser): best-effort
      // cross-origin call to revoke server-side tokens. Send
      // credentials so it can clear the cookie when app + auth share a site.
      // Whatever it returns, the LOCAL teardown below must still run — a
      // CORS-blocked or unreachable /logout must never leave the user
      // visibly signed in.
      let revokeError: AuthError | null = null
      const accessToken = data.session?.access_token
      if (accessToken) {
        const { error } = await this.api.signOut({
          client_id: this.clientId,
          id_token_hint: data.session?.id_token,
          credentials: 'include'
        })
        if (error) {
          // Surface only real API rejections, and even those AFTER the local
          // teardown. Swallowed entirely: network/CORS failures (cross-site
          // /logout is expected to be blocked — the redirect flow is the
          // robust path), 404s (user might not exist anymore) and 401s (an
          // invalid or expired JWT should sign out the current session).
          if (
            isAuthApiError(error) &&
            error.status !== 404 &&
            error.status !== 401
          ) {
            revokeError = error
          }
        }
      }
      await this._removeSession()
      await this.storage.removeItem(`${this.storageKey}-code-verifier`)
      await this.clearPendingPasswordless()
      await this._notifyAllSubscribers('SIGNED_OUT', null)
      return { error: revokeError }
    })
  }

  /**
   * Subscribes to auth-state changes for this client.
   *
   * The callback fires for `INITIAL_SESSION` once shortly after subscribing
   * (so consumers don't have to special-case "no event yet"), and then for
   * every `SIGNED_IN`, `SIGNED_OUT`, `TOKEN_REFRESHED`, `PASSWORD_RECOVERY`,
   * and `USER_UPDATED` event. Events are broadcast across tabs through
   * `BroadcastChannel`, so a sign-in or sign-out in one tab reaches every
   * other tab using the same `storageKey`.
   *
   * @param callback Invoked with the event name and the new session (or
   *   `null` on `SIGNED_OUT`). Can return a promise — the SDK awaits it.
   * @returns `{ data: { subscription } }` — call `subscription.unsubscribe()`
   *   to stop listening.
   * @example
   * ```ts
   * const { data: { subscription } } = auth.onAuthStateChange((event, session) => {
   *   if (event === 'SIGNED_IN') console.log('Welcome', session?.user.email)
   * })
   * // later
   * subscription.unsubscribe()
   * ```
   * @see {@link https://faable.com/docs/auth/get-started | Get Started with Faable Auth}
   * @category Sessions
   */
  onAuthStateChange(
    callback: (
      event: AuthChangeEvent,
      session: Session | null
    ) => void | Promise<void>
  ): {
    data: { subscription: Subscription }
  } {
    const { subscription } = this.broadcastSync.subscribe(callback)
    this._debug(
      '#onAuthStateChange()',
      'registered callback with id',
      subscription.id
    )
    ;(async () => {
      await this.initializePromise
      await this.lock._acquireLock(-1, async () => {
        this._emitInitialSession(subscription)
      })
    })()

    return { data: { subscription } }
  }

  private async _emitInitialSession(subscription: Subscription): Promise<void> {
    return await this._useSession(async result => {
      try {
        const {
          data: { session },
          error
        } = result
        if (error) throw error

        await subscription.callback('INITIAL_SESSION', session)
        this._debug(
          'INITIAL_SESSION',
          'callback id',
          subscription.id,
          'session',
          session
        )
      } catch (err) {
        await subscription.callback('INITIAL_SESSION', null)
        this._debug(
          'INITIAL_SESSION',
          'callback id',
          subscription.id,
          'error',
          err
        )
        console.error(err)
      }
    })
  }

  /**
   * Forces a new session by exchanging a refresh token regardless of expiry.
   *
   * Normally the SDK handles refresh transparently via the auto-refresh
   * ticker; call this only when you need to force an immediate refresh —
   * e.g. right after a server-side action that changed the user's claims.
   * Omit `currentSession` to reuse whatever {@link FaableAuthClient.getSession}
   * returns.
   *
   * @param currentSession Optional session shape carrying the refresh token
   *   to exchange. When passed it must include `refresh_token`.
   * @example
   * ```ts
   * const { data, error } = await auth.refreshSession()
   * ```
   * @see {@link https://faable.com/docs/auth/oauth-flows/refresh-token | Refresh Token}
   * @category Sessions
   */
  async refreshSession(currentSession?: {
    refresh_token: string
  }): Promise<AuthResponse> {
    await this.initializePromise

    return await this.lock._acquireLock(-1, async () => {
      return await this._refreshSession(currentSession)
    })
  }

  protected async _refreshSession(currentSession?: {
    refresh_token: string
  }): Promise<AuthResponse> {
    try {
      return await this._useSession(async result => {
        if (!currentSession) {
          const { data, error } = result
          if (error) {
            throw error
          }

          currentSession = data.session ?? undefined
        }

        if (!currentSession?.refresh_token) {
          throw new AuthSessionMissingError()
        }

        const { session, error } = await this._callRefreshToken(
          currentSession.refresh_token
        )
        if (error) {
          return { data: { user: null, session: null }, error: error }
        }

        if (!session) {
          return { data: { user: null, session: null }, error: null }
        }

        return { data: { user: (session as any).user, session }, error: null }
      })
    } catch (error) {
      if (isAuthError(error)) {
        return { data: { user: null, session: null }, error }
      }

      throw error
    }
  }

  /**
   * A live access token without showing a login screen — or the reason there
   * cannot be one.
   *
   * 1. **Refresh first.** The stored refresh token is exchanged at
   *    `/oauth/token` (no navigation). This is the everyday path.
   * 2. **Then `prompt=none`, as a top-level navigation.** When the refresh
   *    is refused — no session, or `invalid_grant` because the session was
   *    revoked: a sign-out on another device, an admin revoke, a
   *    back-channel logout — the SDK sends the browser through
   *    `/authorize?prompt=none`. The auth server signs the user back in from
   *    its SSO cookie without a screen, and the page lands on `redirectTo`
   *    with the user's location in `returnTo` (see
   *    {@link FaableAuthClient.initialize}). On this path the promise **never
   *    resolves**: the page is navigating away.
   *
   * There is no hidden iframe on purpose: third-party cookie blocking makes an
   * iframe to the auth domain unreliable in every major browser, so the silent
   * round-trip is a real navigation the user does not see.
   *
   * When the SSO session is gone too, the server comes back with
   * `?error=login_required`. {@link initialize} keeps that for one
   * {@link getRedirectError} read, and a further `getTokenSilently()` on the
   * same page load resolves with an `AuthLoginRequiredError` instead of
   * navigating again — no loops. A network failure or a 5xx during the
   * refresh is not a refusal: it comes back as the retryable error, and the
   * session is kept.
   *
   * @example
   * ```ts
   * const { data, error } = await auth.getTokenSilently()
   * if (error) {
   *   if (error.code === 'login_required') auth.authorize({ response_type: 'code' })
   *   return
   * }
   * fetch('/api/me', { headers: { Authorization: `Bearer ${data.access_token}` } })
   * ```
   * @category Sessions
   */
  async getTokenSilently(
    options: GetTokenSilently = {}
  ): Promise<
    | { data: { access_token: string; session: Session }; error: null }
    | { data: null; error: AuthError }
  > {
    await this.initializePromise

    const { data, error } = await this.refreshSession()
    if (!error && data.session) {
      return {
        data: {
          access_token: data.session.access_token,
          session: data.session
        },
        error: null
      }
    }
    // No verdict from the server (network, 5xx, timeout): the session is not
    // refused, so navigating away would throw a good session at a blip.
    if (error && isAuthRetryableFetchError(error)) {
      return { data: null, error }
    }

    // This page load already IS the return leg of a silent attempt the
    // server refused. Navigating again would loop forever.
    const last = this._lastInitializeResult?.error as
      | (AuthError & { details?: { error: string } | null })
      | null
      | undefined
    const lastCode = last?.details?.error
    if (
      last?.name === 'AuthImplicitGrantRedirectError' &&
      lastCode &&
      SILENT_AUTH_ERRORS.indexOf(lastCode) !== -1
    ) {
      return { data: null, error: new AuthLoginRequiredError(lastCode) }
    }

    if (options.redirect === false || !isBrowser()) {
      return { data: null, error: new AuthLoginRequiredError() }
    }

    // Never resolves in a browser: the navigation owns the page from here.
    await this._handleConnectionSignIn({
      redirectTo: options.redirectTo,
      returnTo: options.returnTo ?? window?.location.href,
      scopes: options.scope,
      audience: options.audience,
      appState: options.appState,
      queryParams: { ...(options.queryParams ?? {}), prompt: 'none' },
      silent: true
    })
    return { data: null, error: new AuthLoginRequiredError() }
  }
}
